import type { SupportedLanguage } from "../locales";

type HomeFaqOverrideEntry = {
  question: string;
  answer: string;
};

export function getLeadingHomeFaqEntries(language: SupportedLanguage): HomeFaqOverrideEntry[] {
  return [
    leadingHomeFaq[language] ?? leadingHomeFaq.en,
    secondLeadingHomeFaq[language] ?? secondLeadingHomeFaq.en,
  ];
}

function shiftHomeFaqEntries(
  entries: Partial<Record<number, HomeFaqOverrideEntry>> | undefined,
  offset: number,
): Partial<Record<number, HomeFaqOverrideEntry>> {
  if (!entries) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(entries).map(([index, entry]) => [Number(index) + offset, entry]),
  ) as Partial<Record<number, HomeFaqOverrideEntry>>;
}

const leadingHomeFaq: Record<SupportedLanguage, HomeFaqOverrideEntry> = {
  ar: {
    question: "ما هو lip-sync وكيف يعمل lip-sync بالذكاء الاصطناعي؟",
    answer:
      "الـ lip-sync هو سحر وحوش حقيقي 😈 عندما نترجم الفيديو إلى لغة أخرى، يجعل Vevi حركة شفتيك تطابق الصوت الجديد ليبدو كل شيء طبيعيًا. يقوم AI lip-sync لدينا بمزامنة حركة الفم تلقائيًا أثناء ترجمة الفيديو والدبلجة واستبدال الصوت — لدرجة أن والدتك قد تظن أنك تتحدث هذه اللغة فعلًا ✨",
  },
  bg: {
    question: "Какво е lip-sync и как работи AI lip-sync?",
    answer:
      "Lip-sync е чиста чудовищна магия 😈 Когато превеждаме видеото ви на друг език, Vevi кара устните ви да съвпадат с новия глас, така че всичко да изглежда естествено. Нашият AI lip-sync автоматично синхронизира движенията на устата по време на видео превод, дублаж и смяна на гласа — така естествено, че и майка ви може да реши, че наистина говорите този език ✨",
  },
  cs: {
    question: "Co je lip-sync a jak funguje AI lip-sync?",
    answer:
      "Lip-sync je čistá monster magie 😈 Když přeložíme vaše video do jiného jazyka, Vevi sladí pohyb rtů s novým hlasem tak, aby to působilo přirozeně. Náš AI lip-sync automaticky synchronizuje pohyby úst během překladu videa, dabingu i nahrazení hlasu — tak dobře, že si i vaše máma může myslet, že ten jazyk opravdu umíte ✨",
  },
  da: {
    question: "Hvad er lip-sync, og hvordan fungerer AI lip-sync?",
    answer:
      "Lip-sync er ren monstermagi 😈 Når vi oversætter din video til et andet sprog, får Vevi dine læber til at passe til den nye stemme, så det føles naturligt. Vores AI lip-sync synkroniserer automatisk mundbevægelser under videooversættelse, dubbing og stemmeudskiftning — så naturligt, at selv din mor måske tror, du faktisk taler sproget ✨",
  },
  de: {
    question: "Was ist Lip-Sync und wie funktioniert AI Lip-Sync?",
    answer:
      "Lip-Sync ist pure Monster-Magie 😈 Wenn wir Ihr Video in eine andere Sprache übersetzen, sorgt Vevi dafür, dass Ihre Lippen zur neuen Stimme passen, sodass alles natürlich wirkt. Unser AI Lip-Sync synchronisiert Mundbewegungen automatisch bei Videoübersetzung, Dubbing und Stimmenersatz — so gut, dass selbst Ihre Mutter denken könnte, Sie sprechen diese Sprache wirklich ✨",
  },
  el: {
    question: "Τι είναι το lip-sync και πώς λειτουργεί το AI lip-sync;",
    answer:
      "Το lip-sync είναι καθαρή monster μαγεία 😈 Όταν μεταφράζουμε το βίντεό σας σε άλλη γλώσσα, το Vevi κάνει τα χείλη σας να ταιριάζουν με τη νέα φωνή ώστε όλα να φαίνονται φυσικά. Το AI lip-sync μας συγχρονίζει αυτόματα τις κινήσεις του στόματος κατά τη μετάφραση βίντεο, το dubbing και την αντικατάσταση φωνής — τόσο καλά, που ακόμα και η μαμά σας μπορεί να νομίζει ότι πράγματι μιλάτε αυτή τη γλώσσα ✨",
  },
  en: {
    question: "What is lip-sync and how does AI lip-sync work?",
    answer:
      "Lip-sync is pure monster magic 😈 When we translate your video into another language, Vevi makes your lips match the new voice so it feels natural. Our AI lip-sync automatically syncs mouth movements during video translation, dubbing, and voice replacement — so even your mom might think you actually speak that language ✨",
  },
  es: {
    question: "¿Qué es el lip-sync y cómo funciona el lip-sync con IA?",
    answer:
      "El lip-sync es pura magia monstruosa 😈 Cuando traducimos tu video a otro idioma, Vevi hace que tus labios coincidan con la nueva voz para que todo se sienta natural. Nuestro AI lip-sync sincroniza automáticamente los movimientos de la boca durante la traducción de video, el doblaje y el reemplazo de voz — tan bien que hasta tu mamá podría pensar que de verdad hablas ese idioma ✨",
  },
  et: {
    question: "Mis on lip-sync ja kuidas AI lip-sync töötab?",
    answer:
      "Lip-sync on puhas monstrimaagia 😈 Kui tõlgime sinu video teise keelde, paneb Vevi sinu huuled uue häälega kokku sobima, et tulemus tunduks loomulik. Meie AI lip-sync sünkroonib suuliigutused automaatselt video tõlke, dubleerimise ja häälevahetuse ajal — nii hästi, et isegi su ema võib arvata, et sa räägid seda keelt päriselt ✨",
  },
  fi: {
    question: "Mitä lip-sync on ja miten AI lip-sync toimii?",
    answer:
      "Lip-sync on puhdasta hirviötaikaa 😈 Kun käännämme videosi toiselle kielelle, Vevi saa huulesi osumaan uuteen ääneen niin, että lopputulos tuntuu luonnolliselta. AI lip-syncimme synkronoi suun liikkeet automaattisesti videon käännön, dubbauksen ja äänenvaihdon aikana — niin hyvin, että jopa äitisi voisi luulla sinun oikeasti puhuvan sitä kieltä ✨",
  },
  fr: {
    question: "Qu'est-ce que le lip-sync et comment fonctionne le lip-sync IA ?",
    answer:
      "Le lip-sync, c'est de la pure magie de monstre 😈 Quand on traduit votre vidéo dans une autre langue, Vevi fait correspondre vos lèvres à la nouvelle voix pour que tout paraisse naturel. Notre AI lip-sync synchronise automatiquement les mouvements de bouche pendant la traduction vidéo, le doublage et le remplacement de voix — au point que même votre mère pourrait croire que vous parlez vraiment cette langue ✨",
  },
  ga: {
    question: "Cad é lip-sync agus conas a oibríonn AI lip-sync?",
    answer:
      "Is draíocht ollphéist ghlan é lip-sync 😈 Nuair a aistrímid do fhíseán go teanga eile, cuireann Vevi do liopaí ag teacht leis an nguth nua ionas go mothaíonn sé nádúrtha. Déanann ár nAI lip-sync gluaiseachtaí an bhéil a shioncronú go huathoibríoch le linn aistriú físe, dubbing agus athsholáthar gutha — chomh maith sin go gceapfadh do mháthair féin go labhraíonn tú an teanga sin i ndáiríre ✨",
  },
  hr: {
    question: "Što je lip-sync i kako radi AI lip-sync?",
    answer:
      "Lip-sync je čista monster magija 😈 Kad prevedemo vaš video na drugi jezik, Vevi usklađuje vaše usne s novim glasom kako bi sve djelovalo prirodno. Naš AI lip-sync automatski sinkronizira pokrete usta tijekom prijevoda videa, dubbinga i zamjene glasa — toliko prirodno da bi i vaša mama mogla pomisliti da stvarno govorite taj jezik ✨",
  },
  hu: {
    question: "Mi az a lip-sync, és hogyan működik az AI lip-sync?",
    answer:
      "A lip-sync tiszta szörnymágia 😈 Amikor lefordítjuk a videódat egy másik nyelvre, a Vevi úgy igazítja az ajkaid mozgását az új hanghoz, hogy teljesen természetesnek hasson. Az AI lip-sync automatikusan szinkronizálja a szájmozgást videófordítás, szinkron és hangcsere közben — olyan jól, hogy még az anyukád is azt hiheti, tényleg beszéled azt a nyelvet ✨",
  },
  it: {
    question: "Che cos'è il lip-sync e come funziona l'AI lip-sync?",
    answer:
      "Il lip-sync è pura magia mostruosa 😈 Quando traduciamo il tuo video in un'altra lingua, Vevi fa combaciare le tue labbra con la nuova voce, così tutto sembra naturale. Il nostro AI lip-sync sincronizza automaticamente i movimenti della bocca durante la traduzione video, il doppiaggio e la sostituzione della voce — così bene che persino tua madre potrebbe pensare che tu parli davvero quella lingua ✨",
  },
  ja: {
    question: "lip-syncとは何ですか？AI lip-syncはどう動くのですか？",
    answer:
      "lip-syncはまさにモンスター級の魔法です 😈 動画を別の言語に翻訳するとき、Veviが唇の動きを新しい音声に合わせて、自然に見えるようにします。私たちのAI lip-syncは、動画翻訳、吹き替え、音声置き換えの最中に口の動きを自動で同期します。あまりに自然なので、お母さんでさえ本当にその言語を話していると思うかもしれません ✨",
  },
  ko: {
    question: "립싱크가 뭐고, AI 립싱크는 어떻게 작동하나요?",
    answer:
      "립싱크는 말 그대로 몬스터급 마법이에요 😈 영상을 다른 언어로 번역할 때 Vevi가 입 모양을 새 목소리에 맞춰서 훨씬 자연스럽게 보이게 해줍니다. 우리 AI 립싱크는 영상 번역, 더빙, 음성 교체 과정에서 입 움직임을 자동으로 맞춰줘서 엄마조차 정말 그 언어를 하는 줄 알 수도 있어요 ✨",
  },
  lt: {
    question: "Kas yra lip-sync ir kaip veikia AI lip-sync?",
    answer:
      "Lip-sync yra tikra monstrų magija 😈 Kai išverčiame jūsų vaizdo įrašą į kitą kalbą, Vevi priderina lūpų judesius prie naujo balso, kad viskas atrodytų natūraliai. Mūsų AI lip-sync automatiškai sinchronizuoja burnos judesius vaizdo vertimo, dubliavimo ir balso pakeitimo metu — taip gerai, kad net jūsų mama gali pamanyti, jog tikrai kalbate ta kalba ✨",
  },
  lv: {
    question: "Kas ir lip-sync un kā darbojas AI lip-sync?",
    answer:
      "Lip-sync ir īsta monstru maģija 😈 Kad mēs iztulkojam jūsu video citā valodā, Vevi pielāgo lūpu kustības jaunajai balsij, lai viss izskatītos dabiski. Mūsu AI lip-sync automātiski sinhronizē mutes kustības video tulkošanas, dublēšanas un balss nomaiņas laikā — tik labi, ka pat jūsu mamma varētu noticēt, ka jūs tiešām runājat tajā valodā ✨",
  },
  mt: {
    question: "X'inhu l-lip-sync u kif jaħdem l-AI lip-sync?",
    answer:
      "Il-lip-sync huwa maġija vera tal-monsters 😈 Meta nittraduċu l-vidjow tiegħek f'lingwa oħra, Vevi jqabbel il-moviment tax-xofftejn mal-vuċi l-ġdida biex kollox jidher naturali. L-AI lip-sync tagħna jissinkronizza awtomatikament il-movimenti tal-ħalq waqt it-traduzzjoni tal-vidjow, id-dubbing u s-sostituzzjoni tal-vuċi — tant tajjeb li anke ommok tista' taħseb li tassew titkellem dik il-lingwa ✨",
  },
  nl: {
    question: "Wat is lip-sync en hoe werkt AI lip-sync?",
    answer:
      "Lip-sync is pure monstermagie 😈 Wanneer we je video naar een andere taal vertalen, laat Vevi je lippen aansluiten op de nieuwe stem zodat het natuurlijk voelt. Onze AI lip-sync synchroniseert mondbewegingen automatisch tijdens videovertaling, dubbing en stemvervanging — zo goed dat zelfs je moeder misschien denkt dat je die taal echt spreekt ✨",
  },
  pl: {
    question: "Czym jest lip-sync i jak działa AI lip-sync?",
    answer:
      "Lip-sync to czysta potworna magia 😈 Kiedy tłumaczymy Twoje wideo na inny język, Vevi sprawia, że ruch ust pasuje do nowego głosu, więc wszystko wygląda naturalnie. Nasz AI lip-sync automatycznie synchronizuje ruchy ust podczas tłumaczenia wideo, dubbingu i podmiany głosu — tak dobrze, że nawet Twoja mama może pomyśleć, że naprawdę mówisz w tym języku ✨",
  },
  pt: {
    question: "O que é lip-sync e como funciona o AI lip-sync?",
    answer:
      "Lip-sync é pura magia monstruosa 😈 Quando traduzimos seu vídeo para outro idioma, a Vevi faz seus lábios combinarem com a nova voz para que tudo pareça natural. Nosso AI lip-sync sincroniza automaticamente os movimentos da boca durante a tradução de vídeo, a dublagem e a substituição de voz — tão bem que até sua mãe pode achar que você realmente fala aquele idioma ✨",
  },
  ro: {
    question: "Ce este lip-sync și cum funcționează AI lip-sync?",
    answer:
      "Lip-sync-ul este magie monstruoasă pură 😈 Când traducem videoclipul tău într-o altă limbă, Vevi face ca buzele tale să se potrivească cu noua voce, astfel încât totul să pară natural. AI lip-sync-ul nostru sincronizează automat mișcările gurii în timpul traducerii video, dublajului și înlocuirii vocii — atât de bine încât până și mama ta ar putea crede că vorbești cu adevărat limba respectivă ✨",
  },
  ru: {
    question: "Что такое lip-sync и как работает AI lip-sync?",
    answer:
      "Lip-sync — это чистая монстромагия 😈 Когда мы переводим ваше видео на другой язык, Vevi подгоняет движение губ под новый голос, чтобы всё выглядело естественно. Наш AI lip-sync автоматически синхронизирует движения рта во время перевода видео, дубляжа и замены голоса — настолько хорошо, что даже мама может решить, будто вы и правда говорите на этом языке ✨",
  },
  sk: {
    question: "Čo je lip-sync a ako funguje AI lip-sync?",
    answer:
      "Lip-sync je čistá monster mágia 😈 Keď preložíme vaše video do iného jazyka, Vevi zosúladí pohyb pier s novým hlasom, aby to pôsobilo prirodzene. Náš AI lip-sync automaticky synchronizuje pohyby úst počas prekladu videa, dabingu a nahradenia hlasu — tak dobre, že si aj vaša mama môže myslieť, že ten jazyk naozaj ovládate ✨",
  },
  sl: {
    question: "Kaj je lip-sync in kako deluje AI lip-sync?",
    answer:
      "Lip-sync je čista monster magija 😈 Ko vaš video prevedemo v drug jezik, Vevi uskladi vaše ustnice z novim glasom, da vse deluje naravno. Naš AI lip-sync samodejno usklajuje premike ust med prevajanjem videa, sinhronizacijo in zamenjavo glasu — tako dobro, da bi še vaša mama lahko mislila, da ta jezik res govorite ✨",
  },
  sv: {
    question: "Vad är lip-sync och hur fungerar AI lip-sync?",
    answer:
      "Lip-sync är ren monstermagi 😈 När vi översätter din video till ett annat språk ser Vevi till att dina läppar matchar den nya rösten så att allt känns naturligt. Vår AI lip-sync synkar automatiskt munrörelser under videoöversättning, dubbning och röstbyte — så bra att till och med din mamma kanske tror att du faktiskt talar språket ✨",
  },
  zh: {
    question: "什么是 lip-sync，AI lip-sync 是怎么工作的？",
    answer:
      "lip-sync 就是纯纯的怪兽魔法 😈 当我们把你的视频翻译成另一种语言时，Vevi 会让你的嘴型和新的声音匹配起来，让一切看起来自然流畅。我们的 AI lip-sync 会在视频翻译、配音和语音替换过程中自动同步口型——自然到连你妈妈都可能以为你真的会说那门语言 ✨",
  },
};

const secondLeadingHomeFaq: Record<SupportedLanguage, HomeFaqOverrideEntry> = {
  ar: {
    question: "ما هو AI dubbing أو AI voice over؟",
    answer:
      "AI dubbing هو سحر وحوش حقيقي 😈 يقوم Vevi بترجمة الفيديو تلقائيًا واستبدال صوتك بلغة أخرى — من دون إعادة تسجيل أو تعديل إضافي. ومع AI lip-sync واستبدال الصوت، يمكن لصنّاع المحتوى تحويل Short واحد إلى محتوى عالمي، والوصول إلى جماهير جديدة، وزيادة المشاهدات من دون إنتاج فيديوهات جديدة ✨",
  },
  bg: {
    question: "Какво е AI dubbing или AI voice over?",
    answer:
      "AI dubbing е чудовищна магия 😈 Vevi автоматично превежда видеото ви и заменя гласа ви на друг език — без презапис и без допълнителен монтаж. С AI lip-sync и подмяна на гласа създателите могат да превърнат едно Short във глобално съдържание, да достигнат нови аудитории и да увеличат гледанията, без да снимат нови видеа ✨",
  },
  cs: {
    question: "Co je AI dubbing nebo AI voice over?",
    answer:
      "AI dubbing je čistá monster magie 😈 Vevi automaticky přeloží vaše video a nahradí váš hlas jiným jazykem — bez nového nahrávání a bez další editace. Díky AI lip-syncu a nahrazení hlasu mohou tvůrci proměnit jeden Short v globální obsah, oslovit nové publikum a získat více zhlédnutí bez natáčení nových videí ✨",
  },
  da: {
    question: "Hvad er AI dubbing eller AI voice over?",
    answer:
      "AI dubbing er ren monstermagi 😈 Vevi oversætter automatisk din video og erstatter din stemme på et andet sprog — uden genindspilning og uden ekstra redigering. Med AI lip-sync og stemmeudskiftning kan skabere forvandle én Short til globalt indhold, nå nye målgrupper og få flere visninger uden at lave nye videoer ✨",
  },
  de: {
    question: "Was ist AI Dubbing oder AI Voice-over?",
    answer:
      "AI Dubbing ist pure Monster-Magie 😈 Vevi übersetzt Ihr Video automatisch und ersetzt Ihre Stimme in einer anderen Sprache — ganz ohne Neuaufnahme und ohne zusätzliche Bearbeitung. Mit AI Lip-Sync und Stimmenersatz können Creator aus einem einzigen Short globalen Content machen, neue Zielgruppen erreichen und mehr Views holen, ohne neue Videos zu produzieren ✨",
  },
  el: {
    question: "Τι είναι το AI dubbing ή το AI voice over;",
    answer:
      "Το AI dubbing είναι καθαρή monster μαγεία 😈 Το Vevi μεταφράζει αυτόματα το βίντεό σας και αντικαθιστά τη φωνή σας σε άλλη γλώσσα — χωρίς νέα ηχογράφηση και χωρίς επιπλέον μοντάζ. Με AI lip-sync και αντικατάσταση φωνής, οι creators μπορούν να μετατρέψουν ένα μόνο Short σε παγκόσμιο περιεχόμενο, να φτάσουν σε νέο κοινό και να αυξήσουν τις προβολές χωρίς να φτιάξουν νέα βίντεο ✨",
  },
  en: {
    question: "What is AI dubbing or AI voice over?",
    answer:
      "AI dubbing is monster magic 😈 Vevi automatically translates your video and replaces your voice in another language — no re-recording, no extra editing. With AI lip-sync and voice replacement, creators can turn one Short into global content, reach new audiences, and grow views without making new videos ✨",
  },
  es: {
    question: "¿Qué es el AI dubbing o AI voice over?",
    answer:
      "El AI dubbing es pura magia monstruosa 😈 Vevi traduce tu video automáticamente y reemplaza tu voz en otro idioma — sin volver a grabar y sin edición extra. Con AI lip-sync y reemplazo de voz, los creadores pueden convertir un solo Short en contenido global, llegar a nuevas audiencias y aumentar las vistas sin hacer videos nuevos ✨",
  },
  et: {
    question: "Mis on AI dubbing või AI voice over?",
    answer:
      "AI dubbing on puhas monstrimaagia 😈 Vevi tõlgib su video automaatselt ja vahetab su hääle teise keele vastu välja — ilma uuesti salvestamata ja ilma lisamontaažita. AI lip-sync ja häälevahetus aitavad loojatel muuta ühe Shorti globaalseks sisuks, jõuda uute vaatajateni ja kasvatada vaatamisi ilma uusi videoid tegemata ✨",
  },
  fi: {
    question: "Mitä AI dubbing tai AI voice over tarkoittaa?",
    answer:
      "AI dubbing on puhdasta hirviötaikaa 😈 Vevi kääntää videosi automaattisesti ja korvaa äänesi toisella kielellä — ilman uudelleenäänitystä ja ilman ylimääräistä editointia. AI lip-syncin ja äänenvaihdon avulla tekijät voivat muuttaa yhden Shortin globaaliksi sisällöksi, tavoittaa uusia yleisöjä ja kasvattaa katseluita ilman uusien videoiden tekemistä ✨",
  },
  fr: {
    question: "Qu'est-ce que l'AI dubbing ou l'AI voice over ?",
    answer:
      "L'AI dubbing, c'est de la magie de monstre 😈 Vevi traduit automatiquement votre vidéo et remplace votre voix dans une autre langue — sans réenregistrement et sans montage supplémentaire. Avec l'AI lip-sync et le remplacement de voix, les créateurs peuvent transformer un seul Short en contenu global, toucher de nouvelles audiences et faire grimper les vues sans produire de nouvelles vidéos ✨",
  },
  ga: {
    question: "Cad é AI dubbing nó AI voice over?",
    answer:
      "Is draíocht ollphéist é AI dubbing 😈 Aistríonn Vevi do fhíseán go huathoibríoch agus cuireann sé do ghuth in áit i dteanga eile — gan aththaifeadadh ná eagarthóireacht bhreise. Le AI lip-sync agus athsholáthar gutha, is féidir le cruthaitheoirí Short amháin a iompú ina ábhar domhanda, lucht féachana nua a bhaint amach agus níos mó radharcanna a fháil gan físeáin nua a dhéanamh ✨",
  },
  hr: {
    question: "Što je AI dubbing ili AI voice over?",
    answer:
      "AI dubbing je čista monster magija 😈 Vevi automatski prevodi vaš video i zamjenjuje vaš glas na drugom jeziku — bez ponovnog snimanja i bez dodatne obrade. Uz AI lip-sync i zamjenu glasa, kreatori mogu pretvoriti jedan Short u globalni sadržaj, dosegnuti novu publiku i povećati preglede bez snimanja novih videa ✨",
  },
  hu: {
    question: "Mi az AI dubbing vagy az AI voice over?",
    answer:
      "Az AI dubbing tiszta szörnymágia 😈 A Vevi automatikusan lefordítja a videódat, és egy másik nyelven lecseréli a hangodat — újrafelvétel és extra vágás nélkül. Az AI lip-sync és a hangcsere segítségével az alkotók egyetlen Shortból globális tartalmat csinálhatnak, új közönségeket érhetnek el, és növelhetik a megtekintéseket új videók készítése nélkül ✨",
  },
  it: {
    question: "Che cos'è l'AI dubbing o l'AI voice over?",
    answer:
      "L'AI dubbing è pura magia mostruosa 😈 Vevi traduce automaticamente il tuo video e sostituisce la tua voce in un'altra lingua — senza riregistrare e senza editing extra. Con AI lip-sync e sostituzione della voce, i creator possono trasformare un solo Short in contenuto globale, raggiungere nuovi pubblici e aumentare le visualizzazioni senza creare nuovi video ✨",
  },
  ja: {
    question: "AI dubbingやAI voice overとは何ですか？",
    answer:
      "AI dubbingはまさにモンスター級の魔法です 😈 Veviは動画を自動で翻訳し、あなたの声を別の言語の音声に置き換えます。再録音も追加編集も不要です。AI lip-syncと音声置き換えによって、クリエイターは1本のShortをグローバル向けコンテンツに変え、新しい視聴者に届き、新しい動画を作らずに再生数を伸ばせます ✨",
  },
  ko: {
    question: "AI 더빙이나 AI 보이스오버가 뭔가요?",
    answer:
      "AI 더빙은 몬스터급 마법이에요 😈 Vevi가 영상을 자동으로 번역하고 여러분의 목소리를 다른 언어로 바꿔줘요 — 다시 녹음할 필요도 없고 추가 편집도 필요 없어요. AI 립싱크와 음성 교체 덕분에 크리에이터는 Short 하나를 글로벌 콘텐츠로 바꾸고, 새로운 시청자에게 도달하고, 새 영상을 만들지 않고도 조회수를 키울 수 있어요 ✨",
  },
  lt: {
    question: "Kas yra AI dubbing arba AI voice over?",
    answer:
      "AI dubbing yra tikra monstrų magija 😈 Vevi automatiškai išverčia jūsų vaizdo įrašą ir pakeičia jūsų balsą kita kalba — be pakartotinio įrašymo ir be papildomo redagavimo. Su AI lip-sync ir balso pakeitimu kūrėjai gali vieną Short paversti pasauliniu turiniu, pasiekti naujas auditorijas ir auginti peržiūras nekurdami naujų vaizdo įrašų ✨",
  },
  lv: {
    question: "Kas ir AI dubbing vai AI voice over?",
    answer:
      "AI dubbing ir īsta monstru maģija 😈 Vevi automātiski iztulko jūsu video un aizstāj jūsu balsi citā valodā — bez atkārtotas ierakstīšanas un bez papildu montāžas. Ar AI lip-sync un balss nomaiņu satura veidotāji var pārvērst vienu Short par globālu saturu, sasniegt jaunas auditorijas un palielināt skatījumus, neveidojot jaunus video ✨",
  },
  mt: {
    question: "X'inhu AI dubbing jew AI voice over?",
    answer:
      "AI dubbing huwa maġija vera tal-monsters 😈 Vevi jittraduċi l-vidjow tiegħek awtomatikament u jissostitwixxi l-vuċi tiegħek b'lingwa oħra — mingħajr re-recording u mingħajr editjar addizzjonali. Bl-AI lip-sync u s-sostituzzjoni tal-vuċi, il-creators jistgħu jdawru Short wieħed f'kontenut globali, jilħqu udjenzi ġodda u jkabbru l-views mingħajr ma joħolqu vidjows ġodda ✨",
  },
  nl: {
    question: "Wat is AI dubbing of AI voice over?",
    answer:
      "AI dubbing is pure monstermagie 😈 Vevi vertaalt je video automatisch en vervangt je stem in een andere taal — zonder opnieuw op te nemen en zonder extra montage. Met AI lip-sync en stemvervanging kunnen creators van één Short globale content maken, nieuwe doelgroepen bereiken en meer views halen zonder nieuwe video's te produceren ✨",
  },
  pl: {
    question: "Czym jest AI dubbing albo AI voice over?",
    answer:
      "AI dubbing to czysta potworna magia 😈 Vevi automatycznie tłumaczy Twoje wideo i podmienia Twój głos na inny język — bez ponownego nagrywania i bez dodatkowego montażu. Dzięki AI lip-sync i podmianie głosu twórcy mogą zamienić jeden Short w globalny content, dotrzeć do nowych odbiorców i zwiększać wyświetlenia bez tworzenia nowych filmów ✨",
  },
  pt: {
    question: "O que é AI dubbing ou AI voice over?",
    answer:
      "AI dubbing é pura magia monstruosa 😈 A Vevi traduz seu vídeo automaticamente e substitui sua voz em outro idioma — sem regravação e sem edição extra. Com AI lip-sync e substituição de voz, creators podem transformar um único Short em conteúdo global, alcançar novas audiências e aumentar as visualizações sem produzir vídeos novos ✨",
  },
  ro: {
    question: "Ce este AI dubbing sau AI voice over?",
    answer:
      "AI dubbing este magie monstruoasă pură 😈 Vevi îți traduce automat videoclipul și îți înlocuiește vocea într-o altă limbă — fără reînregistrare și fără editare suplimentară. Cu AI lip-sync și înlocuire de voce, creatorii pot transforma un singur Short în conținut global, pot ajunge la audiențe noi și pot crește vizualizările fără să facă videoclipuri noi ✨",
  },
  ru: {
    question: "Что такое AI dubbing или AI voice over?",
    answer:
      "AI dubbing — это чистая монстромагия 😈 Vevi автоматически переводит ваше видео и заменяет ваш голос на другой язык — без перезаписи и без лишнего монтажа. Благодаря AI lip-sync и замене голоса авторы могут превратить один Short в глобальный контент, выйти на новую аудиторию и увеличить просмотры без создания новых видео ✨",
  },
  sk: {
    question: "Čo je AI dubbing alebo AI voice over?",
    answer:
      "AI dubbing je čistá monster mágia 😈 Vevi automaticky preloží vaše video a nahradí váš hlas iným jazykom — bez nového nahrávania a bez ďalšej úpravy. Vďaka AI lip-syncu a nahradeniu hlasu môžu tvorcovia premeniť jeden Short na globálny obsah, osloviť nové publikum a zvýšiť pozretia bez natáčania nových videí ✨",
  },
  sl: {
    question: "Kaj je AI dubbing ali AI voice over?",
    answer:
      "AI dubbing je čista monster magija 😈 Vevi samodejno prevede tvoj video in zamenja tvoj glas v drugem jeziku — brez ponovnega snemanja in brez dodatnega urejanja. Z AI lip-syncom in zamenjavo glasu lahko ustvarjalci en Short spremenijo v globalno vsebino, dosežejo novo občinstvo in povečajo oglede brez ustvarjanja novih videov ✨",
  },
  sv: {
    question: "Vad är AI dubbing eller AI voice over?",
    answer:
      "AI dubbing är ren monstermagi 😈 Vevi översätter din video automatiskt och ersätter din röst på ett annat språk — utan ominspelning och utan extra redigering. Med AI lip-sync och röstbyte kan creators förvandla en enda Short till globalt innehåll, nå nya målgrupper och få fler visningar utan att skapa nya videor ✨",
  },
  zh: {
    question: "什么是 AI dubbing 或 AI voice over？",
    answer:
      "AI dubbing 就是纯纯的怪兽魔法 😈 Vevi 会自动翻译你的视频，并把你的声音替换成另一种语言——不用重新录音，也不用额外剪辑。借助 AI lip-sync 和语音替换，创作者可以把一个 Short 变成全球化内容，触达更多新观众，在不用制作新视频的情况下提升播放量 ✨",
  },
};

const baseHomeFaqOverrides: Partial<
  Record<SupportedLanguage, Partial<Record<number, HomeFaqOverrideEntry>>>
> = {
  ar: {
    0: {
      question: "هل تترجمون الفيديو بالكامل أم الصوت فقط؟",
      answer:
        "نترجم الفيديو، ونستبدل الصوت بلغة أخرى، وننظف الصوت، ونضبط مزامنة الشفاه ليبدو كل شيء طبيعيًا.",
    },
    3: {
      question: "هل الفيديو أطول من 30 ثانية؟",
      answer:
        "ليس بعد 😅 الحد الأقصى حاليًا 30 ثانية. مثالي لـ Shorts وReels وTikTok.",
    },
    5: {
      question: "كم تكلفة ترجمة Short؟",
      answer:
        "فقط 2 دولار لShort حتى 30 ثانية — ويشمل ذلك الترجمة وتنظيف الصوت ومزامنة الشفاه بشكل طبيعي.",
    },
  },
  bg: {
    0: {
      question: "Превеждате видеото или само гласа?",
      answer:
        "Превеждаме видеото, заменяме гласа на друг език, почистваме аудиото и синхронизираме устните, за да изглежда и звучи всичко естествено.",
    },
    3: {
      question: "Видео по-дълго от 30 сек?",
      answer:
        "Не 😅 Засега максимум 30 сек. Идеално за Shorts, Reels и TikTok.",
    },
    5: {
      question: "Колко струва преводът на един Short?",
      answer:
        "Само $2 за Short до 30 секунди — включително превод, почистване на аудиото и естествен lip-sync.",
    },
  },
  cs: {
    0: {
      question: "Překládáte video, nebo jen hlas?",
      answer:
        "Přeložíme vaše video, nahradíme hlas v jiném jazyce, vyčistíme audio a sladíme pohyb rtů, aby vše působilo přirozeně.",
    },
    3: {
      question: "Video delší než 30 s?",
      answer:
        "Zatím ne 😅 Prozatím maximálně 30 s. Ideální pro Shorts, Reels a TikTok.",
    },
    5: {
      question: "Kolik stojí přeložit Short?",
      answer:
        "Jen 2 $ za Short do 30 sekund — včetně překladu, vyčištění audia a přirozeného lip-syncu.",
    },
  },
  da: {
    0: {
      question: "Oversætter I videoer eller kun stemmen?",
      answer:
        "Vi oversætter din video, udskifter stemmen til et andet sprog, renser lyden og synkroniserer læberne, så det hele virker naturligt.",
    },
    3: {
      question: "Video længere end 30 sek.?",
      answer:
        "Nej 😅 Maks. 30 sek. lige nu. Perfekt til Shorts, Reels og TikTok.",
    },
    5: {
      question: "Hvad koster det at oversætte en Short?",
      answer:
        "Kun 2 $ for en Short på op til 30 sekunder — inklusive oversættelse, lydrensning og naturlig lip-sync.",
    },
  },
  de: {
    0: {
      question: "Übersetzen Sie Videos oder nur die Stimme?",
      answer:
        "Wir übersetzen Ihr Video, ersetzen die Stimme in einer anderen Sprache, bereinigen das Audio und passen die Lippenbewegungen an, damit alles natürlich wirkt.",
    },
    3: {
      question: "Video länger als 30 Sek.?",
      answer:
        "Nein 😅 Im Moment maximal 30 Sek. Perfekt für Shorts, Reels und TikTok.",
    },
    5: {
      question: "Was kostet die Übersetzung eines Shorts?",
      answer:
        "Nur 2 $ für einen Short bis zu 30 Sekunden — inklusive Übersetzung, Audiobereinigung und natürlicher Lippensynchronisation.",
    },
  },
  el: {
    0: {
      question: "Μεταφράζετε το βίντεο ή μόνο τη φωνή;",
      answer:
        "Μεταφράζουμε το βίντεό σας, αντικαθιστούμε τη φωνή σε άλλη γλώσσα, καθαρίζουμε τον ήχο και συγχρονίζουμε τα χείλη, ώστε όλα να παραμένουν φυσικά.",
    },
    3: {
      question: "Βίντεο μεγαλύτερο από 30 δευτ.;",
      answer:
        "Όχι ακόμη 😅 Προς το παρόν έως 30 δευτ. Ιδανικό για Shorts, Reels και TikTok.",
    },
    5: {
      question: "Πόσο κοστίζει η μετάφραση ενός Short;",
      answer:
        "Μόνο 2 $ για ένα Short έως 30 δευτερόλεπτα — περιλαμβάνει μετάφραση, καθαρισμό ήχου και φυσικό lip-sync.",
    },
  },
  en: {
    0: {
      question: "Do you translate videos or just voice?",
      answer:
        "We translate your video, replace the voice in another language, clean audio, and lip-sync it to keep everything natural.",
    },
    3: {
      question: "Video longer than 30 sec?",
      answer:
        "Nope 😅 30 sec max for now. Perfect for Shorts, Reels & TikTok.",
    },
    5: {
      question: "How much does it cost to translate a Short?",
      answer:
        "Just $2 for a Short up to 30 seconds — including translation, audio cleanup, and natural lip-sync.",
    },
  },
  es: {
    0: {
      question: "¿Traducen videos o solo la voz?",
      answer:
        "Traducimos tu video, reemplazamos la voz en otro idioma, limpiamos el audio y ajustamos el lip-sync para que todo se vea y suene natural.",
    },
    3: {
      question: "¿Video de más de 30 seg?",
      answer:
        "Todavía no 😅 Por ahora, máximo 30 seg. Perfecto para Shorts, Reels y TikTok.",
    },
    5: {
      question: "¿Cuánto cuesta traducir un Short?",
      answer:
        "Solo $2 por un Short de hasta 30 segundos — incluye traducción, limpieza de audio y lip-sync natural.",
    },
  },
  et: {
    0: {
      question: "Kas te tõlgite videoid või ainult häält?",
      answer:
        "Me tõlgime teie video, vahetame hääle teise keelde, puhastame heli ja sünkroonime huulte liikumise, et kõik kõlaks ja näeks loomulik välja.",
    },
    3: {
      question: "Video pikem kui 30 sek?",
      answer:
        "Veel mitte 😅 Praegu kuni 30 sek. Ideaalne Shorts'i, Reelsi ja TikToki jaoks.",
    },
    5: {
      question: "Kui palju maksab Shorti tõlkimine?",
      answer:
        "Ainult 2 $ kuni 30-sekundilise Shorti eest — hinna sees on tõlge, heli puhastamine ja loomulik lip-sync.",
    },
  },
  fi: {
    0: {
      question: "Käännättekö videon vai pelkän äänen?",
      answer:
        "Käännämme videosi, vaihdamme äänen toiselle kielelle, siistimme audion ja synkronoimme huulten liikkeet, jotta kaikki tuntuu luonnolliselta.",
    },
    3: {
      question: "Yli 30 sekunnin video?",
      answer:
        "Ei vielä 😅 Tällä hetkellä enintään 30 sekuntia. Täydellinen Shortseihin, Reelseihin ja TikTokiin.",
    },
    5: {
      question: "Mitä Shortin kääntäminen maksaa?",
      answer:
        "Vain 2 $ enintään 30 sekunnin Shortista — mukana käännös, äänen siistiminen ja luonnollinen lip-sync.",
    },
  },
  fr: {
    0: {
      question: "Traduisez-vous les vidéos ou seulement la voix ?",
      answer:
        "Nous traduisons votre vidéo, remplaçons la voix dans une autre langue, nettoyons l'audio et synchronisons les lèvres pour que tout reste naturel.",
    },
    3: {
      question: "Vidéo de plus de 30 s ?",
      answer:
        "Pas encore 😅 30 s max pour le moment. Parfait pour les Shorts, Reels et TikTok.",
    },
    5: {
      question: "Combien coûte la traduction d'un Short ?",
      answer:
        "Seulement 2 $ pour un Short jusqu'à 30 secondes — traduction, nettoyage audio et synchronisation labiale naturelle inclus.",
    },
  },
  ga: {
    0: {
      question: "An n-aistríonn sibh físeáin nó an guth amháin?",
      answer:
        "Aistrímid do fhíseán, cuirimid guth nua air i dteanga eile, glanaimid an fhuaim agus déanaimid sioncronú liopaí chun go mbeidh gach rud nádúrtha.",
    },
    3: {
      question: "Físeán níos faide ná 30 soic?",
      answer:
        "Ní hea fós 😅 30 soic ar a mhéad faoi láthair. Foirfe do Shorts, Reels agus TikTok.",
    },
    5: {
      question: "Cé mhéid a chosnaíonn sé Short a aistriú?",
      answer:
        "Níl ann ach $2 do Short suas le 30 soicind — lena n-áirítear aistriúchán, glanadh fuaime agus sioncronú liopaí nádúrtha.",
    },
  },
  hr: {
    0: {
      question: "Prevodite li video ili samo glas?",
      answer:
        "Prevodimo vaš video, zamjenjujemo glas na drugom jeziku, čistimo zvuk i usklađujemo pokrete usana kako bi sve zvučalo i izgledalo prirodno.",
    },
    3: {
      question: "Video dulji od 30 sek?",
      answer:
        "Ne 😅 Za sada je maksimum 30 sek. Savršeno za Shorts, Reels i TikTok.",
    },
    5: {
      question: "Koliko košta prijevod jednog Shorta?",
      answer:
        "Samo 2 $ za Short do 30 sekundi — uključujući prijevod, čišćenje zvuka i prirodan lip-sync.",
    },
  },
  hu: {
    0: {
      question: "Videót fordítotok, vagy csak a hangot?",
      answer:
        "Lefordítjuk a videódat, másik nyelvre cseréljük a hangot, megtisztítjuk az audiót, és a szájmozgást is összehangoljuk, hogy minden természetes maradjon.",
    },
    3: {
      question: "30 mp-nél hosszabb videó?",
      answer:
        "Még nem 😅 Egyelőre legfeljebb 30 mp. Tökéletes Shorts, Reels és TikTok videókhoz.",
    },
    5: {
      question: "Mennyibe kerül egy Short lefordítása?",
      answer:
        "Mindössze 2 $ egy legfeljebb 30 másodperces Shortért — benne van a fordítás, az audiótisztítás és a természetes lip-sync.",
    },
  },
  it: {
    0: {
      question: "Traducete i video o solo la voce?",
      answer:
        "Traduciamo il tuo video, sostituiamo la voce in un'altra lingua, puliamo l'audio e sincronizziamo il labiale per mantenere tutto naturale.",
    },
    3: {
      question: "Video più lungo di 30 sec?",
      answer:
        "Non ancora 😅 Per ora massimo 30 sec. Perfetto per Shorts, Reels e TikTok.",
    },
    5: {
      question: "Quanto costa tradurre uno Short?",
      answer:
        "Solo 2 $ per uno Short fino a 30 secondi — inclusi traduzione, pulizia audio e lip-sync naturale.",
    },
  },
  ja: {
    0: {
      question: "動画全体を翻訳するのですか？それとも音声だけですか？",
      answer:
        "動画を翻訳し、別の言語の音声に置き換え、音声を整え、リップシンクまで行うので、全体が自然に仕上がります。",
    },
    3: {
      question: "30秒を超える動画は？",
      answer:
        "まだ対応していません 😅 今は最大30秒です。Shorts、Reels、TikTokにぴったりです。",
    },
    5: {
      question: "Shortを翻訳する料金はいくらですか？",
      answer:
        "30秒までのShortなら2ドルだけです。翻訳、音声クリーンアップ、自然なリップシンクが含まれます。",
    },
  },
  ko: {
    0: {
      question: "영상 전체를 번역하나요, 아니면 음성만 처리하나요?",
      answer:
        "영상 내용을 번역하고, 다른 언어로 음성을 교체하고, 오디오를 정리하고, 립싱크까지 맞춰서 전체적으로 자연스럽게 만듭니다.",
    },
    3: {
      question: "30초가 넘는 영상도 되나요?",
      answer:
        "아직은 아니에요 😅 지금은 최대 30초예요. Shorts, Reels, TikTok에 딱 맞습니다.",
    },
    5: {
      question: "Short 하나 번역하는 데 얼마인가요?",
      answer:
        "30초 이하 Short 하나당 단 2달러예요 — 번역, 오디오 정리, 자연스러운 립싱크가 모두 포함됩니다.",
    },
  },
  lt: {
    0: {
      question: "Ar verčiate vaizdo įrašą, ar tik balsą?",
      answer:
        "Išverčiame jūsų vaizdo įrašą, pakeičiame balsą kita kalba, sutvarkome garsą ir suderiname lūpų judesius, kad viskas skambėtų ir atrodytų natūraliai.",
    },
    3: {
      question: "Vaizdo įrašas ilgesnis nei 30 sek.?",
      answer:
        "Dar ne 😅 Kol kas daugiausia 30 sek. Puikiai tinka Shorts, Reels ir TikTok.",
    },
    5: {
      question: "Kiek kainuoja išversti Short?",
      answer:
        "Vos 2 $ už iki 30 sekundžių Short — į kainą įeina vertimas, garso sutvarkymas ir natūralus lip-sync.",
    },
  },
  lv: {
    0: {
      question: "Vai jūs tulkojat video vai tikai balsi?",
      answer:
        "Mēs iztulkojam jūsu video, nomainām balsi uz citu valodu, attīrām audio un sinhronizējam lūpu kustības, lai viss izklausītos un izskatītos dabiski.",
    },
    3: {
      question: "Video garāks par 30 sek?",
      answer:
        "Vēl nē 😅 Pagaidām maksimums ir 30 sek. Ideāli Shorts, Reels un TikTok.",
    },
    5: {
      question: "Cik maksā Short tulkošana?",
      answer:
        "Tikai 2 $ par Short līdz 30 sekundēm — cenā iekļauts tulkojums, audio attīrīšana un dabisks lip-sync.",
    },
  },
  mt: {
    0: {
      question: "Tittraduċu l-vidjow jew il-vuċi biss?",
      answer:
        "Nittraduċu l-vidjow tiegħek, nibdlu l-vuċi f'lingwa oħra, innaddfu l-awdjo u naqblu l-lip-sync biex kollox jidher u jinstema' naturali.",
    },
    3: {
      question: "Vidjow itwal minn 30 sek?",
      answer:
        "Mhux bħalissa 😅 Bħalissa massimu ta' 30 sek. Perfett għal Shorts, Reels u TikTok.",
    },
    5: {
      question: "Kemm tiswa t-traduzzjoni ta' Short?",
      answer:
        "Biss $2 għal Short sa 30 sekonda — inklużi traduzzjoni, tindif tal-awdjo u lip-sync naturali.",
    },
  },
  nl: {
    0: {
      question: "Vertalen jullie video's of alleen de stem?",
      answer:
        "We vertalen je video, vervangen de stem in een andere taal, schonen de audio op en synchroniseren de lippen zodat alles natuurlijk blijft.",
    },
    3: {
      question: "Video langer dan 30 sec?",
      answer:
        "Nog niet 😅 Voor nu maximaal 30 sec. Perfect voor Shorts, Reels en TikTok.",
    },
    5: {
      question: "Wat kost het om een Short te vertalen?",
      answer:
        "Slechts $2 voor een Short tot 30 seconden — inclusief vertaling, audio-opruiming en natuurlijke lip-sync.",
    },
  },
  pl: {
    0: {
      question: "Tłumaczycie wideo czy tylko głos?",
      answer:
        "Tłumaczymy Twoje wideo, podmieniamy głos na inny język, czyścimy audio i dopasowujemy ruch ust, żeby całość brzmiała i wyglądała naturalnie.",
    },
    3: {
      question: "Wideo dłuższe niż 30 sek.?",
      answer:
        "Nie 😅 Na razie maksymalnie 30 sek. Idealne do Shorts, Reels i TikToka.",
    },
    5: {
      question: "Ile kosztuje przetłumaczenie Shorta?",
      answer:
        "Tylko 2 $ za Short do 30 sekund — w cenie tłumaczenie, czyszczenie audio i naturalny lip-sync.",
    },
  },
  pt: {
    0: {
      question: "Vocês traduzem vídeos ou só a voz?",
      answer:
        "Traduzimos seu vídeo, substituímos a voz em outro idioma, limpamos o áudio e fazemos o lip-sync para que tudo fique natural.",
    },
    3: {
      question: "Vídeo com mais de 30 seg?",
      answer:
        "Ainda não 😅 Máximo de 30 seg por enquanto. Perfeito para Shorts, Reels e TikTok.",
    },
    5: {
      question: "Quanto custa traduzir um Short?",
      answer:
        "Apenas $2 por um Short de até 30 segundos — incluindo tradução, limpeza de áudio e lip-sync natural.",
    },
  },
  ro: {
    0: {
      question: "Traduceți videoclipuri sau doar vocea?",
      answer:
        "Traducem videoclipul, înlocuim vocea în altă limbă, curățăm audio-ul și sincronizăm buzele pentru ca totul să rămână natural.",
    },
    3: {
      question: "Clip mai lung de 30 sec?",
      answer:
        "Încă nu 😅 Deocamdată maxim 30 sec. Perfect pentru Shorts, Reels și TikTok.",
    },
    5: {
      question: "Cât costă traducerea unui Short?",
      answer:
        "Doar 2 $ pentru un Short de până la 30 de secunde — cu traducere, curățare audio și lip-sync natural incluse.",
    },
  },
  ru: {
    0: {
      question: "Вы переводите видео или только голос?",
      answer:
        "Мы переводим ваше видео, заменяем голос на другом языке, очищаем аудио и синхронизируем губы, чтобы всё звучало и выглядело естественно.",
    },
    3: {
      question: "Видео длиннее 30 сек?",
      answer:
        "Нет 😅 Пока максимум 30 сек. Идеально для Shorts, Reels и TikTok.",
    },
    5: {
      question: "Сколько стоит перевести Short?",
      answer:
        "Всего $2 за Short длиной до 30 секунд — включая перевод, очистку аудио и естественный lip-sync.",
    },
  },
  sk: {
    0: {
      question: "Prekladáte video alebo iba hlas?",
      answer:
        "Preložíme vaše video, nahradíme hlas v inom jazyku, vyčistíme audio a zosúladíme pohyb pier, aby všetko pôsobilo prirodzene.",
    },
    3: {
      question: "Video dlhšie ako 30 s?",
      answer:
        "Zatiaľ nie 😅 Momentálne maximálne 30 s. Perfektné pre Shorts, Reels a TikTok.",
    },
    5: {
      question: "Koľko stojí preklad Shortu?",
      answer:
        "Len 2 $ za Short do 30 sekúnd — vrátane prekladu, vyčistenia audia a prirodzeného lip-syncu.",
    },
  },
  sl: {
    0: {
      question: "Prevajate video ali samo glas?",
      answer:
        "Prevedemo vaš video, zamenjamo glas v drugem jeziku, očistimo zvok in uskladimo gibanje ustnic, da vse ostane naravno.",
    },
    3: {
      question: "Video daljši od 30 sek?",
      answer:
        "Za zdaj ne 😅 Trenutno največ 30 sek. Popolno za Shorts, Reels in TikTok.",
    },
    5: {
      question: "Koliko stane prevod Shorta?",
      answer:
        "Samo 2 $ za Short do 30 sekund — vključno s prevodom, čiščenjem zvoka in naravnim lip-syncom.",
    },
  },
  sv: {
    0: {
      question: "Översätter ni videor eller bara rösten?",
      answer:
        "Vi översätter din video, byter ut rösten till ett annat språk, rensar ljudet och synkar läpprörelserna så att allt känns naturligt.",
    },
    3: {
      question: "Video längre än 30 sek?",
      answer:
        "Inte ännu 😅 Max 30 sek just nu. Perfekt för Shorts, Reels och TikTok.",
    },
    5: {
      question: "Vad kostar det att översätta en Short?",
      answer:
        "Bara 2 $ för en Short på upp till 30 sekunder — inklusive översättning, ljudrensning och naturlig lip-sync.",
    },
  },
  zh: {
    0: {
      question: "你们是翻译整个视频，还是只处理语音？",
      answer:
        "我们会翻译你的视频、替换为另一种语言的配音、清理音频，并做好口型同步，让整体效果自然流畅。",
    },
    3: {
      question: "视频超过30秒？",
      answer:
        "暂时还不行 😅 目前最长30秒。非常适合 Shorts、Reels 和 TikTok。",
    },
    5: {
      question: "翻译一个 Short 要多少钱？",
      answer:
        "只需 2 美元，即可翻译一个 30 秒以内的 Short — 包含翻译、音频清理和自然口型同步。",
    },
  },
};

const paymentBeforeFaqOverrides: Record<SupportedLanguage, HomeFaqOverrideEntry> = {
  ar: {
    question: "هل أدفع قبل الدبلجة بالذكاء الاصطناعي واستبدال الصوت؟",
    answer:
      "نعم، قبل ذلك ☕ لكي تبدأ السحر، يحتاج الذكاء الاصطناعي لدينا إلى قليل من الطاقة. بأقل من ثمن فنجان قهوة، يقوم Vevi Monster بترجمة الفيديو واستبدال صوتك وضبط مزامنة الشفاه بلغة أخرى.",
  },
  bg: {
    question: "Плаща ли преди AI дублажа и смяната на гласа?",
    answer:
      "Да, предварително ☕ За да започне магията, нашият AI има нужда от малко енергия. За по-малко от цената на кафе Vevi Monster превежда видеото, заменя гласа ви и прави lip-sync на друг език.",
  },
  cs: {
    question: "Platím před AI dabingem a nahrazením hlasu?",
    answer:
      "Ano, předem ☕ Aby se mohla spustit ta magie, naše AI potřebuje trochu energie. Za méně než stojí káva Vevi Monster přeloží vaše video, nahradí váš hlas a sladí lip-sync v jiném jazyce.",
  },
  da: {
    question: "Betaler jeg før AI-dubbing og stemmeudskiftning?",
    answer:
      "Ja, på forhånd ☕ For at sætte magien i gang har vores AI brug for lidt energi. For mindre end en kop kaffe oversætter Vevi Monster din video, erstatter din stemme og laver lip-sync på et andet sprog.",
  },
  de: {
    question: "Muss ich vor dem KI-Dubbing und dem Stimmenersatz bezahlen?",
    answer:
      "Ja, vorher ☕ Damit die Magie starten kann, braucht unsere KI ein bisschen Energie. Für weniger als einen Kaffee übersetzt Vevi Monster Ihr Video, ersetzt Ihre Stimme und passt den Lip-Sync in einer anderen Sprache an.",
  },
  el: {
    question: "Πληρώνω πριν από το AI dubbing και την αντικατάσταση φωνής;",
    answer:
      "Ναι, πριν ☕ Για να ξεκινήσει η μαγεία, η AI μας χρειάζεται λίγη ενέργεια. Με λιγότερα από έναν καφέ, το Vevi Monster μεταφράζει το βίντεό σας, αντικαθιστά τη φωνή σας και συγχρονίζει τα χείλη σε άλλη γλώσσα.",
  },
  en: {
    question: "Do I pay before AI dubbing and voice replacement?",
    answer:
      "Before ☕ To start the magic, our AI needs a little energy. For less than a cup of coffee, Vevi Monster translates, replaces your voice, and lip-syncs your video into another language.",
  },
  es: {
    question: "¿Pago antes del doblaje con IA y del reemplazo de voz?",
    answer:
      "Antes ☕ Para poner en marcha la magia, nuestra IA necesita un poquito de energía. Por menos de lo que cuesta un café, Vevi Monster traduce tu video, sustituye tu voz y ajusta el lip-sync en otro idioma.",
  },
  et: {
    question: "Kas ma maksan enne AI-dubleerimist ja hääle asendamist?",
    answer:
      "Jah, enne ☕ Et maagia saaks alata, vajab meie AI veidi energiat. Vähema kui tassitäie kohvi eest tõlgib Vevi Monster su video, asendab su hääle ja sünkroonib huulte liikumise teises keeles.",
  },
  fi: {
    question: "Maksetaanko ennen AI-dubbausta ja äänen korvaamista?",
    answer:
      "Kyllä, etukäteen ☕ Jotta taika pääsee alkuun, tekoälymme tarvitsee hieman energiaa. Vähemmällä kuin kahvikupin hinnalla Vevi Monster kääntää videosi, korvaa äänesi ja synkronoi huulten liikkeet toiselle kielelle.",
  },
  fr: {
    question: "Est-ce que je paie avant le doublage IA et le remplacement de voix ?",
    answer:
      "Oui, avant ☕ Pour lancer la magie, notre IA a besoin d'un peu d'énergie. Pour moins qu'un café, Vevi Monster traduit votre vidéo, remplace votre voix et synchronise les lèvres dans une autre langue.",
  },
  ga: {
    question: "An íocaim roimh dhúbláil AI agus athsholáthar gutha?",
    answer:
      "Roimh ré ☕ Chun an draíocht a chur ag obair, teastaíonn beagán fuinnimh ónár AI. Ar níos lú ná costas cupáin caife, aistríonn Vevi Monster do fhíseán, cuireann sé do ghuth ina áit agus déanann sé lip-sync i dteanga eile.",
  },
  hr: {
    question: "Plaćam li prije AI dubbinga i zamjene glasa?",
    answer:
      "Da, prije ☕ Da pokrenemo čaroliju, našem AI-ju treba malo energije. Za manje od cijene kave Vevi Monster prevodi vaš video, zamjenjuje vaš glas i usklađuje lip-sync na drugom jeziku.",
  },
  hu: {
    question: "Az AI-szinkron és a hangcsere előtt kell fizetnem?",
    answer:
      "Igen, előre ☕ Hogy beinduljon a varázslat, az AI-nknak kell egy kis energia. Egy kávé áránál kevesebbért a Vevi Monster lefordítja a videódat, lecseréli a hangodat és lip-syncet készít más nyelven.",
  },
  it: {
    question: "Pago prima del doppiaggio AI e della sostituzione della voce?",
    answer:
      "Prima ☕ Per far partire la magia, la nostra AI ha bisogno di un po' di energia. Per meno di un caffè, Vevi Monster traduce il tuo video, sostituisce la tua voce e sincronizza il labiale in un'altra lingua.",
  },
  ja: {
    question: "AI吹き替えと音声置き換えの前に支払いが必要ですか？",
    answer:
      "はい、先払いです ☕ 魔法を始めるには、AIに少しだけエネルギーが必要です。コーヒー1杯より安い料金で、Vevi Monsterが動画を翻訳し、あなたの声に置き換え、別の言語でリップシンクまで整えます。",
  },
  ko: {
    question: "AI 더빙과 음성 교체 전에 결제하나요?",
    answer:
      "네, 먼저 결제해요 ☕ 마법을 시작하려면 AI에 약간의 에너지가 필요하거든요. 커피 한 잔보다 적은 금액으로 Vevi Monster가 영상을 번역하고, 목소리를 바꾸고, 다른 언어로 립싱크까지 맞춰줍니다.",
  },
  lt: {
    question: "Ar mokama prieš AI dubliavimą ir balso pakeitimą?",
    answer:
      "Taip, iš anksto ☕ Kad prasidėtų magija, mūsų AI reikia šiek tiek energijos. Už mažiau nei puodelį kavos Vevi Monster išverčia jūsų vaizdo įrašą, pakeičia jūsų balsą ir pritaiko lip-sync kita kalba.",
  },
  lv: {
    question: "Vai es maksāju pirms AI dublēšanas un balss nomaiņas?",
    answer:
      "Jā, iepriekš ☕ Lai sāktos maģija, mūsu AI vajag mazliet enerģijas. Par mazāk nekā kafijas tasi Vevi Monster iztulko jūsu video, nomaina jūsu balsi un pielāgo lip-sync citā valodā.",
  },
  mt: {
    question: "Inħallas qabel l-AI dubbing u s-sostituzzjoni tal-vuċi?",
    answer:
      "Iva, minn qabel ☕ Biex tibda l-maġija, l-AI tagħna teħtieġ ftit enerġija. Bi prezz inqas minn kikkra kafè, Vevi Monster jittraduċi l-vidjow tiegħek, jibdel il-vuċi tiegħek u jqabbel il-lip-sync f'lingwa oħra.",
  },
  nl: {
    question: "Betaal ik vóór AI-dubbing en stemvervanging?",
    answer:
      "Ja, vooraf ☕ Om de magie te laten beginnen, heeft onze AI een beetje energie nodig. Voor minder dan een kop koffie vertaalt Vevi Monster je video, vervangt je stem en doet de lip-sync in een andere taal.",
  },
  pl: {
    question: "Czy płacę przed dubbingiem AI i podmianą głosu?",
    answer:
      "Tak, przed ☕ Żeby uruchomić tę magię, nasze AI potrzebuje odrobiny energii. Za mniej niż koszt kawy Vevi Monster przetłumaczy Twoje wideo, podmieni Twój głos i dopasuje lip-sync w innym języku.",
  },
  pt: {
    question: "Eu pago antes da dublagem com IA e da substituição de voz?",
    answer:
      "Antes ☕ Para a mágica começar, nossa IA precisa de um pouco de energia. Por menos do que um café, a Vevi Monster traduz seu vídeo, substitui sua voz e faz o lip-sync em outro idioma.",
  },
  ro: {
    question: "Plătesc înainte de dublajul AI și înlocuirea vocii?",
    answer:
      "Da, înainte ☕ Ca să pornească magia, AI-ul nostru are nevoie de puțină energie. Cu mai puțin decât costă o cafea, Vevi Monster îți traduce videoclipul, îți înlocuiește vocea și face lip-sync într-o altă limbă.",
  },
  ru: {
    question: "Нужно ли платить до AI-дубляжа и замены голоса?",
    answer:
      "Да, заранее ☕ Чтобы запустить магию, нашему AI нужна небольшая порция энергии. Дешевле чашки кофе Vevi Monster переведёт ваше видео, заменит ваш голос и синхронизирует lip-sync на другом языке.",
  },
  sk: {
    question: "Platím pred AI dabingom a nahradením hlasu?",
    answer:
      "Áno, vopred ☕ Aby sa mohla spustiť táto mágia, naše AI potrebuje trochu energie. Za menej než stojí šálka kávy Vevi Monster preloží vaše video, nahradí váš hlas a zosúladí lip-sync v inom jazyku.",
  },
  sl: {
    question: "Plačam pred AI sinhronizacijo in zamenjavo glasu?",
    answer:
      "Da, vnaprej ☕ Da se čarovnija sploh začne, naš AI potrebuje malo energije. Za manj kot skodelico kave Vevi Monster prevede vaš video, zamenja vaš glas in uskladi lip-sync v drugem jeziku.",
  },
  sv: {
    question: "Betalar jag före AI-dubbning och röstbyte?",
    answer:
      "Ja, i förväg ☕ För att få igång magin behöver vår AI lite energi. För mindre än en kopp kaffe översätter Vevi Monster din video, byter ut din röst och lägger lip-sync på ett annat språk.",
  },
  zh: {
    question: "我是在 AI 配音和语音替换前付款吗？",
    answer:
      "是的，先付款 ☕ 想让魔法开始，我们的 AI 需要一点点能量。花不到一杯咖啡的钱，Vevi Monster 就能帮你翻译视频、替换你的声音，并在另一种语言中完成口型同步。",
  },
};

const refundFaqOverrides: Record<SupportedLanguage, HomeFaqOverrideEntry> = {
  ar: {
    question: "استرداد الأموال؟",
    answer:
      "طبعًا 😎 لم تعجبك النتيجة؟ لا مشكلة — نوفر استردادًا خلال 24 ساعة من الدفع. فقط راسلنا عبر دردشة الموقع أو عبر البريد site@vevi.monster",
  },
  bg: {
    question: "Възстановяване на сума?",
    answer:
      "Разбира се 😎 Ако магията не ви е впечатлила, няма проблем — възстановяване е възможно до 24 часа след плащане. Просто ни пишете в чата на сайта или на site@vevi.monster",
  },
  cs: {
    question: "Vrácení peněz?",
    answer:
      "Jasně 😎 Nejste spokojeni s výsledkem? Žádný problém — refundace je možná do 24 hodin po zaplacení. Stačí nám napsat do chatu na webu nebo na site@vevi.monster",
  },
  da: {
    question: "Refundering?",
    answer:
      "Selvfølgelig 😎 Ikke tilfreds med resultatet? Intet problem — refundering er mulig inden for 24 timer efter betaling. Skriv bare til os i chatten på sitet eller på site@vevi.monster",
  },
  de: {
    question: "Rückerstattung?",
    answer:
      "Na klar 😎 Nicht zufrieden mit der Magie? Kein Problem — Rückerstattungen sind bis 24 Stunden nach der Zahlung möglich. Schreiben Sie uns einfach im Website-Chat oder an site@vevi.monster",
  },
  el: {
    question: "Επιστροφή χρημάτων;",
    answer:
      "Φυσικά 😎 Δεν σας άρεσε το αποτέλεσμα; Κανένα πρόβλημα — μπορείτε να ζητήσετε επιστροφή χρημάτων μέσα σε 24 ώρες από την πληρωμή. Απλώς στείλτε μας μήνυμα στο chat του site ή στο site@vevi.monster",
  },
  en: {
    question: "Refunds?",
    answer:
      "Of course 😎 Not happy with the magic? No worries — refunds are available within 24 hours after payment. Just ping us in the site chat or email us at site@vevi.monster",
  },
  es: {
    question: "¿Reembolsos?",
    answer:
      "Claro 😎 ¿No te convenció la magia? Sin problema — puedes pedir un reembolso dentro de las 24 horas posteriores al pago. Escríbenos por el chat del sitio o a site@vevi.monster",
  },
  et: {
    question: "Raha tagasi?",
    answer:
      "Muidugi 😎 Kui tulemus ei meeldinud, pole hullu — tagastus on võimalik 24 tunni jooksul pärast makset. Kirjuta meile lihtsalt saidi vestlusesse või aadressile site@vevi.monster",
  },
  fi: {
    question: "Hyvitykset?",
    answer:
      "Totta kai 😎 Etkö ollut tyytyväinen lopputulokseen? Ei hätää — hyvitys on mahdollinen 24 tunnin sisällä maksusta. Laita meille viesti sivuston chatissa tai osoitteeseen site@vevi.monster",
  },
  fr: {
    question: "Remboursements ?",
    answer:
      "Bien sûr 😎 Pas convaincu par la magie ? Aucun souci — un remboursement est possible dans les 24 heures suivant le paiement. Écrivez-nous simplement dans le chat du site ou à site@vevi.monster",
  },
  ga: {
    question: "Aisíocaíochtaí?",
    answer:
      "Cinnte 😎 Nach raibh tú sásta leis an toradh? Ná bí buartha — tá aisíocaíocht ar fáil laistigh de 24 uair an chloig tar éis íocaíochta. Ní gá ach teachtaireacht a chur chugainn sa chomhrá ar an suíomh nó ag site@vevi.monster",
  },
  hr: {
    question: "Povrat novca?",
    answer:
      "Naravno 😎 Niste zadovoljni rezultatom? Nema problema — povrat je moguć unutar 24 sata od plaćanja. Samo nam se javite u chatu na stranici ili na site@vevi.monster",
  },
  hu: {
    question: "Visszatérítés?",
    answer:
      "Persze 😎 Nem tetszett a végeredmény? Semmi gond — a fizetést követő 24 órán belül kérhetsz visszatérítést. Csak írj nekünk a weboldal chatjében vagy a site@vevi.monster címre",
  },
  it: {
    question: "Rimborsi?",
    answer:
      "Certo 😎 Non ti ha convinto la magia? Nessun problema — il rimborso è disponibile entro 24 ore dal pagamento. Scrivici nella chat del sito oppure a site@vevi.monster",
  },
  ja: {
    question: "返金はありますか？",
    answer:
      "もちろんです 😎 仕上がりに満足できなかった場合もご安心ください。お支払いから24時間以内であれば返金可能です。サイトのチャットか site@vevi.monster までご連絡ください。",
  },
  ko: {
    question: "환불되나요?",
    answer:
      "물론이죠 😎 결과가 마음에 들지 않아도 괜찮아요 — 결제 후 24시간 이내에는 환불이 가능합니다. 사이트 채팅이나 site@vevi.monster 로 연락해 주세요.",
  },
  lt: {
    question: "Pinigų grąžinimas?",
    answer:
      "Žinoma 😎 Nepatiko rezultatas? Jokių problemų — pinigus grąžiname per 24 valandas po apmokėjimo. Tiesiog parašykite mums svetainės pokalbyje arba el. paštu site@vevi.monster",
  },
  lv: {
    question: "Naudas atmaksa?",
    answer:
      "Protams 😎 Ja rezultāts nepatika, nekas traks — naudas atmaksa ir iespējama 24 stundu laikā pēc maksājuma. Vienkārši uzrakstiet mums vietnes čatā vai uz site@vevi.monster",
  },
  mt: {
    question: "Rifużjoni?",
    answer:
      "Żgur 😎 Mhux kuntent bir-riżultat? Ebda problema — ir-rifużjoni hija disponibbli fi żmien 24 siegħa mill-ħlas. Ibgħatilna messaġġ fil-chat tas-sit jew fuq site@vevi.monster",
  },
  nl: {
    question: "Terugbetaling?",
    answer:
      "Natuurlijk 😎 Niet blij met het resultaat? Geen probleem — terugbetaling is mogelijk binnen 24 uur na betaling. Stuur ons gewoon een bericht in de sitechat of mail naar site@vevi.monster",
  },
  pl: {
    question: "Zwroty?",
    answer:
      "Oczywiście 😎 Nie podoba Ci się efekt? Żaden problem — zwrot jest możliwy w ciągu 24 godzin od płatności. Napisz do nas na czacie na stronie albo na site@vevi.monster",
  },
  pt: {
    question: "Reembolsos?",
    answer:
      "Claro 😎 Não curtiu o resultado? Sem problema — o reembolso está disponível em até 24 horas após o pagamento. É só chamar a gente no chat do site ou escrever para site@vevi.monster",
  },
  ro: {
    question: "Rambursări?",
    answer:
      "Sigur 😎 Nu ți-a plăcut rezultatul? Nicio problemă — rambursarea este disponibilă în termen de 24 de ore de la plată. Scrie-ne în chatul de pe site sau la site@vevi.monster",
  },
  ru: {
    question: "Возврат денег?",
    answer:
      "Конечно 😎 Если результат не понравился, без проблем — возврат доступен в течение 24 часов после оплаты. Просто напишите нам в чате на сайте или на site@vevi.monster",
  },
  sk: {
    question: "Vrátenie peňazí?",
    answer:
      "Samozrejme 😎 Nie ste spokojní s výsledkom? Žiadny problém — vrátenie peňazí je možné do 24 hodín od platby. Stačí nám napísať do chatu na webe alebo na site@vevi.monster",
  },
  sl: {
    question: "Vračilo denarja?",
    answer:
      "Seveda 😎 Niste zadovoljni z rezultatom? Ni problema — vračilo je mogoče v 24 urah po plačilu. Pišite nam v klepet na strani ali na site@vevi.monster",
  },
  sv: {
    question: "Återbetalning?",
    answer:
      "Självklart 😎 Inte nöjd med resultatet? Ingen fara — återbetalning är möjlig inom 24 timmar efter betalning. Skriv bara till oss i chatten på sajten eller till site@vevi.monster",
  },
  zh: {
    question: "可以退款吗？",
    answer:
      "当然可以 😎 如果你对效果不满意，也没问题——付款后 24 小时内都可以申请退款。直接在网站聊天里联系我们，或发邮件到 site@vevi.monster",
  },
};

const supportFaqOverrides: Record<SupportedLanguage, HomeFaqOverrideEntry> = {
  ar: {
    question: "لم تجد إجابتك؟",
    answer:
      "هل تحتاج إلى مساعدة في ترجمة الفيديو بالذكاء الاصطناعي أو استبدال الصوت أو مزامنة الشفاه؟ راسل Vevi في الدردشة وسنساعدك على الوصول إلى جمهور عالمي 😎",
  },
  bg: {
    question: "Не открихте своя отговор?",
    answer:
      "Нуждаете се от помощ с AI превод на видео, смяна на гласа или lip-sync? Пишете на Vevi в чата и ще ви помогнем да излезете глобално 😎",
  },
  cs: {
    question: "Nenašli jste svou odpověď?",
    answer:
      "Potřebujete pomoc s AI překladem videa, nahrazením hlasu nebo lip-syncem? Napište Vevi do chatu a pomůžeme vám vyrazit do světa 😎",
  },
  da: {
    question: "Fandt du ikke dit svar?",
    answer:
      "Har du brug for hjælp til AI-videooversættelse, stemmeudskiftning eller lip-sync? Skriv til Vevi i chatten, så hjælper vi dig med at gå globalt 😎",
  },
  de: {
    question: "Nicht die passende Antwort gefunden?",
    answer:
      "Brauchen Sie Hilfe bei KI-Videoübersetzung, Stimmenersatz oder Lip-Sync? Schreiben Sie Vevi im Chat — wir helfen Ihnen dabei, global durchzustarten 😎",
  },
  el: {
    question: "Δεν βρήκατε την απάντησή σας;",
    answer:
      "Χρειάζεστε βοήθεια με AI μετάφραση βίντεο, αντικατάσταση φωνής ή lip-sync; Στείλτε μήνυμα στη Vevi στο chat και θα σας βοηθήσουμε να βγείτε παγκόσμια 😎",
  },
  en: {
    question: "Didn't find your answer?",
    answer:
      "Need help with AI video translation, voice replacement or lip-sync? Ping Vevi in chat — we’ll help you go global 😎",
  },
  es: {
    question: "¿No encontraste tu respuesta?",
    answer:
      "¿Necesitas ayuda con traducción de video con IA, reemplazo de voz o lip-sync? Escríbele a Vevi en el chat y te ayudamos a llegar a una audiencia global 😎",
  },
  et: {
    question: "Ei leidnud oma vastust?",
    answer:
      "Kas vajad abi AI-videotõlke, häälevahetuse või lip-synciga? Kirjuta Vevile chatis ja aitame sul globaalse publikuni jõuda 😎",
  },
  fi: {
    question: "Etkö löytänyt vastaustasi?",
    answer:
      "Tarvitsetko apua AI-videokäännöksessä, äänen vaihdossa tai lip-syncissä? Laita Veville viestiä chatissa, niin autamme sinua lähtemään maailmalle 😎",
  },
  fr: {
    question: "Vous n'avez pas trouvé votre réponse ?",
    answer:
      "Besoin d'aide pour la traduction vidéo par IA, le remplacement de voix ou le lip-sync ? Écrivez à Vevi dans le chat et on vous aide à passer à l'international 😎",
  },
  ga: {
    question: "Níor aimsigh tú do fhreagra?",
    answer:
      "An bhfuil cabhair uait le haistriú físe AI, athsholáthar gutha nó lip-sync? Cuir teachtaireacht chuig Vevi sa chomhrá agus cabhróimid leat dul domhanda 😎",
  },
  hr: {
    question: "Niste pronašli svoj odgovor?",
    answer:
      "Trebate pomoć s AI prijevodom videa, zamjenom glasa ili lip-syncom? Javite se Vevi u chatu i pomoći ćemo vam da izađete globalno 😎",
  },
  hu: {
    question: "Nem találta meg a választ?",
    answer:
      "Segítség kell AI-videófordításhoz, hangcseréhez vagy lip-synchez? Írj Vevinek a chatben, és segítünk globálissá válni 😎",
  },
  it: {
    question: "Non hai trovato la risposta che cercavi?",
    answer:
      "Hai bisogno di aiuto con traduzione video AI, sostituzione della voce o lip-sync? Scrivi a Vevi in chat e ti aiutiamo a diventare globale 😎",
  },
  ja: {
    question: "探していた答えが見つかりませんか？",
    answer:
      "AI動画翻訳、音声置き換え、リップシンクでお困りですか？ チャットでVeviに声をかけてください。世界に届けるお手伝いをします 😎",
  },
  ko: {
    question: "원하는 답을 못 찾으셨나요?",
    answer:
      "AI 영상 번역, 음성 교체, 립싱크가 필요하신가요? 채팅에서 Vevi에게 말 걸어 주세요. 글로벌 진출을 도와드릴게요 😎",
  },
  lt: {
    question: "Neradote savo atsakymo?",
    answer:
      "Reikia pagalbos su AI vaizdo vertimu, balso pakeitimu ar lip-sync? Parašykite Vevi pokalbyje — padėsime jums išeiti į pasaulį 😎",
  },
  lv: {
    question: "Neatradāt savu atbildi?",
    answer:
      "Vajag palīdzību ar AI video tulkošanu, balss nomaiņu vai lip-sync? Uzrakstiet Vevi čatā — palīdzēsim jums iziet globāli 😎",
  },
  mt: {
    question: "Ma sibtx it-tweġiba tiegħek?",
    answer:
      "Għandek bżonn għajnuna bit-traduzzjoni tal-vidjow bl-AI, bidla tal-vuċi jew lip-sync? Ibgħat messaġġ lil Vevi fiċ-chat u ngħinuk tmur globali 😎",
  },
  nl: {
    question: "Niet gevonden wat je zocht?",
    answer:
      "Hulp nodig bij AI-videovertaling, stemvervanging of lip-sync? Stuur Vevi een bericht in de chat — we helpen je om wereldwijd te gaan 😎",
  },
  pl: {
    question: "Nie znalazłeś odpowiedzi?",
    answer:
      "Potrzebujesz pomocy z tłumaczeniem wideo AI, podmianą głosu albo lip-synciem? Napisz do Vevi na czacie — pomożemy Ci wyjść globalnie 😎",
  },
  pt: {
    question: "Não encontrou sua resposta?",
    answer:
      "Precisa de ajuda com tradução de vídeo por IA, substituição de voz ou lip-sync? Chama a Vevi no chat e a gente te ajuda a ganhar o mundo 😎",
  },
  ro: {
    question: "Nu ai găsit răspunsul?",
    answer:
      "Ai nevoie de ajutor cu traducerea video AI, înlocuirea vocii sau lip-sync? Scrie-i lui Vevi în chat și te ajutăm să mergi global 😎",
  },
  ru: {
    question: "Не нашли свой ответ?",
    answer:
      "Нужна помощь с AI-переводом видео, заменой голоса или lip-sync? Напишите Vevi в чате — поможем вам выйти на глобальную аудиторию 😎",
  },
  sk: {
    question: "Nenašli ste svoju odpoveď?",
    answer:
      "Potrebujete pomoc s AI prekladom videa, nahradením hlasu alebo lip-syncom? Napíšte Vevi do chatu a pomôžeme vám vyjsť do sveta 😎",
  },
  sl: {
    question: "Niste našli svojega odgovora?",
    answer:
      "Potrebujete pomoč pri AI prevajanju videa, zamenjavi glasu ali lip-syncu? Pišite Vevi v chatu in pomagali vam bomo iti globalno 😎",
  },
  sv: {
    question: "Hittade du inte svaret du sökte?",
    answer:
      "Behöver du hjälp med AI-videöversättning, röstbyte eller lip-sync? Skriv till Vevi i chatten så hjälper vi dig att gå globalt 😎",
  },
  zh: {
    question: "没找到你想要的答案？",
    answer:
      "需要 AI 视频翻译、语音替换或口型同步方面的帮助吗？在聊天里联系 Vevi，我们会帮你走向全球 😎",
  },
};

const pronunciationFaqOverrides: Record<SupportedLanguage, HomeFaqOverrideEntry> = {
  ar: {
    question: "يتضمن الفيديو مصطلحات محددة يجب نطقها بشكل صحيح. هل يمكنني التحكم في النطق؟",
    answer:
      "ليس بعد يا صاح 😎 لكن لا تقلق — Vevi Dubbing Monster يصبح أذكى كل يوم. نحن نطوّر باستمرار الدبلجة بالذكاء الاصطناعي، والنطق، واستبدال الصوت، وlip-sync حتى يبدو الـShort المترجم طبيعيًا لدرجة أن والدتك قد تظن أنه النسخة الأصلية ✨",
  },
  bg: {
    question: "Във видеото ми има специфични термини, които трябва да се произнесат правилно. Мога ли да контролирам произношението?",
    answer:
      "Още не, брат 😎 Но спокойно — Vevi Dubbing Monster става по-умен всеки ден. Постоянно подобряваме AI дублажа, произношението, смяната на гласа и lip-sync-а, така че преведеният ви Short да звучи толкова естествено, че и майка ви да помисли, че е оригиналът ✨",
  },
  cs: {
    question: "Moje video obsahuje specifické termíny, které musí být vysloveny správně. Můžu ovlivnit výslovnost?",
    answer:
      "Ještě ne, kámo 😎 Ale v klidu — Vevi Dubbing Monster je každý den chytřejší. Neustále vylepšujeme AI dabing, výslovnost, nahrazení hlasu i lip-sync, aby váš přeložený Short zněl tak přirozeně, že si vaše máma bude myslet, že je to originál ✨",
  },
  da: {
    question: "Min video indeholder specifikke termer, der skal udtales korrekt. Kan jeg styre udtalen?",
    answer:
      "Ikke endnu, ven 😎 Men bare rolig — Vevi Dubbing Monster bliver klogere hver dag. Vi forbedrer hele tiden AI-dubbing, udtale, stemmeudskiftning og lip-sync, så din oversatte Short føles så naturlig, at din mor måske tror, det er originalen ✨",
  },
  de: {
    question: "Mein Video enthält bestimmte Begriffe, die korrekt ausgesprochen werden müssen. Kann ich die Aussprache steuern?",
    answer:
      "Noch nicht, Bro 😎 Aber keine Sorge — Vevi Dubbing Monster wird jeden Tag schlauer. Wir verbessern laufend KI-Dubbing, Aussprache, Stimmenersatz und Lip-Sync, damit sich Ihr übersetzter Short so natürlich anfühlt, dass selbst Ihre Mutter denken könnte, es sei das Original ✨",
  },
  el: {
    question: "Το βίντεό μου περιλαμβάνει συγκεκριμένους όρους που πρέπει να προφέρονται σωστά. Μπορώ να ελέγξω την προφορά;",
    answer:
      "Όχι ακόμα, φίλε 😎 Αλλά μην ανησυχείς — το Vevi Dubbing Monster γίνεται όλο και πιο έξυπνο κάθε μέρα. Βελτιώνουμε συνεχώς το AI dubbing, την προφορά, την αντικατάσταση φωνής και το lip-sync ώστε το μεταφρασμένο σου Short να ακούγεται τόσο φυσικό που μπορεί και η μαμά σου να νομίζει ότι είναι το πρωτότυπο ✨",
  },
  en: {
    question: "My video includes specific terms that must be pronounced correctly. Can I control pronunciation?",
    answer:
      "Not yet, bro 😎 But don’t worry — Vevi Dubbing Monster gets smarter every day. We’re constantly improving AI dubbing, pronunciation, voice replacement, and lip-sync so your translated Short feels so natural your mom might think it’s the original ✨",
  },
  es: {
    question: "Mi video incluye términos específicos que deben pronunciarse correctamente. ¿Puedo controlar la pronunciación?",
    answer:
      "Todavía no, bro 😎 Pero tranqui — Vevi Dubbing Monster se vuelve más inteligente cada día. Mejoramos constantemente el doblaje con IA, la pronunciación, el reemplazo de voz y el lip-sync para que tu Short traducido suene tan natural que hasta tu mamá podría pensar que es el original ✨",
  },
  et: {
    question: "Minu videos on spetsiifilisi termineid, mida tuleb õigesti hääldada. Kas ma saan hääldust kontrollida?",
    answer:
      "Mitte veel, bro 😎 Aga ära muretse — Vevi Dubbing Monster muutub iga päev targemaks. Parandame pidevalt AI-dubleerimist, hääldust, häälevahetust ja lip-synci, et sinu tõlgitud Short tunduks nii loomulik, et isegi su ema võib arvata, et see on originaal ✨",
  },
  fi: {
    question: "Videossani on tiettyjä termejä, jotka pitää lausua oikein. Voinko hallita ääntämystä?",
    answer:
      "Ei vielä, bro 😎 Mutta ei huolta — Vevi Dubbing Monster fiksuuntuu joka päivä. Kehitämme jatkuvasti AI-dubbausta, ääntämystä, äänenvaihtoa ja lip-synciä, jotta käännetty Shortisi tuntuu niin luonnolliselta, että äitisikin voisi luulla sitä alkuperäiseksi ✨",
  },
  fr: {
    question: "Ma vidéo contient des termes précis qui doivent être prononcés correctement. Puis-je contrôler la prononciation ?",
    answer:
      "Pas encore, frérot 😎 Mais pas d'inquiétude — Vevi Dubbing Monster devient plus intelligent chaque jour. On améliore en permanence le doublage IA, la prononciation, le remplacement de voix et le lip-sync pour que ton Short traduit paraisse si naturel que même ta mère pourrait croire que c'est l'original ✨",
  },
  ga: {
    question: "Tá téarmaí ar leith i mo fhíseán a chaithfear a fhuaimniú i gceart. An féidir liom an fhuaimniú a rialú?",
    answer:
      "Ní féidir fós, a chara 😎 Ach ná bí buartha — éiríonn Vevi Dubbing Monster níos cliste gach lá. Táimid i gcónaí ag feabhsú dubbing AI, fuaimniú, athsholáthar gutha agus lip-sync ionas go mothóidh do Short aistrithe chomh nádúrtha sin go gceapfadh do mháthair gur an bunleagan é ✨",
  },
  hr: {
    question: "Moj video sadrži specifične pojmove koji moraju biti pravilno izgovoreni. Mogu li kontrolirati izgovor?",
    answer:
      "Još ne, frende 😎 Ali bez brige — Vevi Dubbing Monster je svakim danom sve pametniji. Stalno unapređujemo AI dubbing, izgovor, zamjenu glasa i lip-sync kako bi tvoj prevedeni Short zvučao toliko prirodno da bi i tvoja mama mogla pomisliti da je original ✨",
  },
  hu: {
    question: "A videómban vannak bizonyos kifejezések, amelyeket helyesen kell kiejteni. Tudom szabályozni a kiejtést?",
    answer:
      "Még nem, bro 😎 De nyugi — a Vevi Dubbing Monster minden nap okosabb lesz. Folyamatosan fejlesztjük az AI-szinkront, a kiejtést, a hangcserét és a lip-syncet, hogy a lefordított Shortod annyira természetes legyen, hogy még anyukád is azt hiheti, hogy az az eredeti ✨",
  },
  it: {
    question: "Il mio video include termini specifici che devono essere pronunciati correttamente. Posso controllare la pronuncia?",
    answer:
      "Non ancora, bro 😎 Ma tranquillo — Vevi Dubbing Monster diventa più intelligente ogni giorno. Miglioriamo continuamente il doppiaggio AI, la pronuncia, la sostituzione della voce e il lip-sync, così il tuo Short tradotto sembrerà così naturale che persino tua madre potrebbe pensare che sia l'originale ✨",
  },
  ja: {
    question: "動画に正しく発音してほしい専門用語が含まれています。発音をコントロールできますか？",
    answer:
      "まだできません、bro 😎 でも心配しないでください。Vevi Dubbing Monsterは毎日どんどん賢くなっています。AI吹き替え、発音、音声置き換え、リップシンクを常に改善しているので、翻訳されたShortがあまりに自然で、お母さんでもオリジナルだと思うかもしれません ✨",
  },
  ko: {
    question: "제 영상에는 정확하게 발음되어야 하는 특정 용어가 들어 있어요. 발음을 제가 제어할 수 있나요?",
    answer:
      "아직은 안 돼요, bro 😎 그래도 걱정 마세요 — Vevi Dubbing Monster는 매일 더 똑똑해지고 있어요. AI 더빙, 발음, 음성 교체, 립싱크를 계속 개선하고 있어서 번역된 Short가 너무 자연스러워서 엄마도 원본인 줄 알 수 있을 정도예요 ✨",
  },
  lt: {
    question: "Mano vaizdo įraše yra specifinių terminų, kurie turi būti ištarti teisingai. Ar galiu valdyti tarimą?",
    answer:
      "Dar ne, bro 😎 Bet nesijaudink — Vevi Dubbing Monster kasdien darosi vis išmanesnis. Nuolat tobuliname AI dubliavimą, tarimą, balso pakeitimą ir lip-sync, kad tavo išverstas Short atrodytų toks natūralus, jog net mama gali pamanyti, kad tai originalas ✨",
  },
  lv: {
    question: "Manā video ir specifiski termini, kuri jāizrunā pareizi. Vai es varu kontrolēt izrunu?",
    answer:
      "Pagaidām vēl ne, bro 😎 Bet neuztraucies — Vevi Dubbing Monster katru dienu kļūst gudrāks. Mēs nepārtraukti uzlabojam AI dublēšanu, izrunu, balss nomaiņu un lip-sync, lai tavs tulkotais Short būtu tik dabisks, ka pat tava mamma varētu domāt, ka tas ir oriģināls ✨",
  },
  mt: {
    question: "Il-vidjow tiegħi jinkludi termini speċifiċi li għandhom jinstemgħu sewwa. Nista' nikkontrolla l-pronunzja?",
    answer:
      "Għadu mhux possibbli, bro 😎 Imma tinkwetax — Vevi Dubbing Monster qed isir aktar intelliġenti kuljum. Aħna kontinwament intejbu d-dubbing bl-AI, il-pronunzja, is-sostituzzjoni tal-vuċi u l-lip-sync biex il-Short tradott tiegħek iħossu daqshekk naturali li anke ommok tista' taħseb li huwa l-oriġinal ✨",
  },
  nl: {
    question: "Mijn video bevat specifieke termen die correct uitgesproken moeten worden. Kan ik de uitspraak sturen?",
    answer:
      "Nog niet, bro 😎 Maar geen zorgen — Vevi Dubbing Monster wordt elke dag slimmer. We verbeteren voortdurend AI-dubbing, uitspraak, stemvervanging en lip-sync, zodat je vertaalde Short zo natuurlijk aanvoelt dat je moeder misschien denkt dat het het origineel is ✨",
  },
  pl: {
    question: "Mój film zawiera konkretne terminy, które muszą być poprawnie wymówione. Czy mogę sterować wymową?",
    answer:
      "Jeszcze nie, bro 😎 Ale spokojnie — Vevi Dubbing Monster z każdym dniem staje się mądrzejszy. Cały czas ulepszamy dubbing AI, wymowę, podmianę głosu i lip-sync, żeby Twój przetłumaczony Short brzmiał tak naturalnie, że nawet Twoja mama mogłaby pomyśleć, że to oryginał ✨",
  },
  pt: {
    question: "Meu vídeo inclui termos específicos que precisam ser pronunciados corretamente. Posso controlar a pronúncia?",
    answer:
      "Ainda não, bro 😎 Mas relaxa — a Vevi Dubbing Monster fica mais inteligente a cada dia. Estamos sempre melhorando dublagem com IA, pronúncia, substituição de voz e lip-sync para que seu Short traduzido fique tão natural que até sua mãe pode achar que é o original ✨",
  },
  ro: {
    question: "Videoclipul meu include termeni specifici care trebuie pronunțați corect. Pot controla pronunția?",
    answer:
      "Încă nu, bro 😎 Dar stai liniștit — Vevi Dubbing Monster devine mai inteligent în fiecare zi. Îmbunătățim constant dublajul AI, pronunția, înlocuirea vocii și lip-sync-ul, astfel încât Shortul tău tradus să pară atât de natural încât până și mama ta ar putea crede că este originalul ✨",
  },
  ru: {
    question: "В моём видео есть специфические термины, которые должны звучать правильно. Могу ли я управлять произношением?",
    answer:
      "Пока нет, бро 😎 Но не переживайте — Vevi Dubbing Monster становится умнее с каждым днём. Мы постоянно улучшаем AI-дубляж, произношение, замену голоса и lip-sync, чтобы ваш переведённый Short звучал настолько естественно, что даже мама может подумать, что это оригинал ✨",
  },
  sk: {
    question: "Moje video obsahuje špecifické termíny, ktoré musia byť vyslovené správne. Môžem ovplyvniť výslovnosť?",
    answer:
      "Zatiaľ ešte nie, bro 😎 Ale bez obáv — Vevi Dubbing Monster je každým dňom múdrejší. Neustále zlepšujeme AI dabing, výslovnosť, nahradenie hlasu aj lip-sync, aby váš preložený Short pôsobil tak prirodzene, že aj vaša mama si môže myslieť, že je to originál ✨",
  },
  sl: {
    question: "Moj video vsebuje posebne izraze, ki morajo biti pravilno izgovorjeni. Ali lahko nadzorujem izgovorjavo?",
    answer:
      "Še ne, bro 😎 Ampak brez skrbi — Vevi Dubbing Monster je vsak dan pametnejši. Ves čas izboljšujemo AI sinhronizacijo, izgovorjavo, zamenjavo glasu in lip-sync, da bo tvoj prevedeni Short zvenel tako naravno, da bo tudi tvoja mama mislila, da je original ✨",
  },
  sv: {
    question: "Min video innehåller specifika termer som måste uttalas korrekt. Kan jag styra uttalet?",
    answer:
      "Inte än, bro 😎 Men oroa dig inte — Vevi Dubbing Monster blir smartare för varje dag. Vi förbättrar ständigt AI-dubbning, uttal, röstbyte och lip-sync så att din översatta Short känns så naturlig att till och med din mamma kanske tror att det är originalet ✨",
  },
  zh: {
    question: "我的视频里有一些必须准确发音的专业术语。我可以控制发音吗？",
    answer:
      "暂时还不行，bro 😎 不过别担心——Vevi Dubbing Monster 每天都在变得更聪明。我们持续改进 AI 配音、发音、语音替换和口型同步，让你翻译后的 Short 自然到连你妈妈都可能以为它就是原版 ✨",
  },
};

const localizedServiceFaqOverrides: Record<
  SupportedLanguage,
  Partial<Record<number, HomeFaqOverrideEntry>>
> = {
  ar: {
    6: {
      question: "هل يمكنني اختيار صوت آخر؟",
      answer: "لا - نحن نستنسخ صوتك أنت فقط.",
    },
    8: {
      question: "كيف أحصل على الفيديو الخاص بي؟",
      answer: "تنزيل فوري في المتصفح + نسخة تصل إلى بريدك الإلكتروني بعد الدفع.",
    },
    9: {
      question: "هل توجد خصومات للطلبات الكبيرة؟",
      answer: "نعم - تواصل مع مساعدنا بالذكاء الاصطناعي في الدردشة المباشرة.",
    },
    11: {
      question: "صيغة غير مدعومة؟",
      answer: "استخدم محولاً أو اطلب مساعدة سريعة من مساعدنا بالذكاء الاصطناعي في الدردشة المباشرة.",
    },
    13: {
      question: "لم يصلني البريد الإلكتروني. ماذا أفعل؟",
      answer:
        "قد يختلف وقت المعالجة حسب حجم الطابور. لا تقلق - ملفك آمن. ستتلقى إشعاراً أو رابط تنزيل مباشراً بمجرد أن يصبح جاهزاً.",
    },
  },
  bg: {
    6: {
      question: "Мога ли да избера друг глас?",
      answer: "Не - клонираме само собствения ви глас.",
    },
    8: {
      question: "Как ще получа видеото си?",
      answer: "Мигновено изтегляне в браузъра + копие на имейла ви след плащане.",
    },
    9: {
      question: "Има ли отстъпки за големи поръчки?",
      answer: "Да - свържете се с нашия AI асистент в онлайн чата.",
    },
    11: {
      question: "Неподдържан формат?",
      answer: "Използвайте конвертор или потърсете бърза помощ от нашия AI асистент в онлайн чата.",
    },
    13: {
      question: "Не получих имейла си. Какво да направя?",
      answer:
        "Времето за обработка може да варира според натоварването на опашката. Не се притеснявайте - файлът ви е в безопасност. Ще получите известие или директен линк за изтегляне веднага щом е готов.",
    },
  },
  cs: {
    6: {
      question: "Mohu si vybrat jiný hlas?",
      answer: "Ne - klonujeme pouze váš vlastní hlas.",
    },
    8: {
      question: "Jak dostanu své video?",
      answer: "Okamžité stažení v prohlížeči + kopie do e-mailu po zaplacení.",
    },
    9: {
      question: "Slevy pro větší objednávky?",
      answer: "Ano - kontaktujte našeho AI asistenta v online chatu.",
    },
    11: {
      question: "Nepodporovaný formát?",
      answer: "Použijte konvertor nebo si vyžádejte rychlou pomoc od našeho AI asistenta v online chatu.",
    },
    13: {
      question: "Nepřišel mi e-mail. Co mám dělat?",
      answer:
        "Doba zpracování se může lišit podle vytížení fronty. Nebojte se - váš soubor je v bezpečí. Jakmile bude vše připravené, dostanete oznámení nebo přímý odkaz ke stažení.",
    },
  },
  da: {
    6: {
      question: "Kan jeg vælge en anden stemme?",
      answer: "Nej - vi kloner kun din egen stemme.",
    },
    8: {
      question: "Hvordan får jeg min video?",
      answer: "Download med det samme i din browser + en kopi til din e-mail efter betaling.",
    },
    9: {
      question: "Rabat ved store bestillinger?",
      answer: "Ja - kontakt vores AI-assistent i onlinechatten.",
    },
    11: {
      question: "Ikke understøttet format?",
      answer: "Brug en konverter, eller få hurtig hjælp fra vores AI-assistent i onlinechatten.",
    },
    13: {
      question: "Jeg har ikke modtaget min e-mail. Hvad gør jeg?",
      answer:
        "Behandlingstiden kan variere alt efter, hvor travl køen er. Bare rolig - din fil er sikker. Du får enten en besked eller et direkte downloadlink, så snart den er klar.",
    },
  },
  de: {
    6: {
      question: "Kann ich eine andere Stimme wählen?",
      answer: "Nein - wir klonen nur Ihre eigene Stimme.",
    },
    8: {
      question: "Wie bekomme ich mein Video?",
      answer: "Sofortiger Download im Browser + eine Kopie per E-Mail nach der Zahlung.",
    },
    9: {
      question: "Rabatte für große Bestellungen?",
      answer: "Ja - kontaktieren Sie unseren AI-Assistenten im Online-Chat.",
    },
    11: {
      question: "Nicht unterstütztes Format?",
      answer: "Nutzen Sie einen Konverter oder holen Sie sich schnelle Hilfe von unserem AI-Assistenten im Online-Chat.",
    },
    13: {
      question: "Ich habe keine E-Mail erhalten. Was soll ich tun?",
      answer:
        "Die Bearbeitungszeit kann je nach Auslastung der Warteschlange variieren. Keine Sorge - Ihre Datei ist sicher. Sie erhalten eine Benachrichtigung oder einen direkten Download-Link, sobald alles fertig ist.",
    },
  },
  el: {
    6: {
      question: "Μπορώ να επιλέξω άλλη φωνή;",
      answer: "Όχι - κλωνοποιούμε μόνο τη δική σας φωνή.",
    },
    8: {
      question: "Πώς θα παραλάβω το βίντεό μου;",
      answer: "Άμεσο κατέβασμα στον browser σας + αντίγραφο στο email σας μετά την πληρωμή.",
    },
    9: {
      question: "Υπάρχουν εκπτώσεις για μεγάλες παραγγελίες;",
      answer: "Ναι - επικοινωνήστε με τον AI βοηθό μας στο online chat.",
    },
    11: {
      question: "Μη υποστηριζόμενη μορφή αρχείου;",
      answer: "Χρησιμοποιήστε μετατροπέα ή ζητήστε γρήγορη βοήθεια από τον AI βοηθό μας στο online chat.",
    },
    13: {
      question: "Δεν έλαβα το email μου. Τι πρέπει να κάνω;",
      answer:
        "Ο χρόνος επεξεργασίας μπορεί να διαφέρει ανάλογα με τον φόρτο της ουράς. Μην ανησυχείτε - το αρχείο σας είναι ασφαλές. Μόλις είναι έτοιμο, θα λάβετε ειδοποίηση ή απευθείας σύνδεσμο λήψης.",
    },
  },
  en: {
    6: {
      question: "Can I choose another voice?",
      answer: "No - we only clone your own voice.",
    },
    8: {
      question: "How do I get my video?",
      answer: "Instant download in your browser + copy to your email after payment.",
    },
    9: {
      question: "Discounts for large orders?",
      answer: "Yes - contact our AI assistant in the online chat.",
    },
    11: {
      question: "Unsupported format?",
      answer: "Use a converter or get quick help from our AI assistant in the online chat.",
    },
    13: {
      question: "I didn't receive my email. What should I do?",
      answer:
        "Processing time may vary due to queue volume. Don't worry - your file is safe. You'll receive either a notification or a direct download link once it's ready.",
    },
  },
  es: {
    6: {
      question: "¿Puedo elegir otra voz?",
      answer: "No: solo clonamos tu propia voz.",
    },
    8: {
      question: "¿Cómo recibo mi video?",
      answer: "Descarga instantánea en tu navegador y copia enviada a tu email después del pago.",
    },
    9: {
      question: "¿Hay descuentos para pedidos grandes?",
      answer: "Sí: contacta a nuestro asistente de IA en el chat online.",
    },
    11: {
      question: "¿Formato no compatible?",
      answer: "Usa un conversor o pide ayuda rápida a nuestro asistente de IA en el chat online.",
    },
    13: {
      question: "No recibí mi email. ¿Qué hago?",
      answer:
        "El tiempo de procesamiento puede variar según el volumen de la cola. No te preocupes: tu archivo está seguro. Recibirás una notificación o un enlace de descarga directo en cuanto esté listo.",
    },
  },
  et: {
    6: {
      question: "Kas ma saan valida teise hääle?",
      answer: "Ei - me kloonime ainult sinu enda häält.",
    },
    8: {
      question: "Kuidas ma oma video kätte saan?",
      answer: "Kohene allalaadimine brauseris + koopia sinu e-postile pärast maksmist.",
    },
    9: {
      question: "Kas suurtele tellimustele on allahindlusi?",
      answer: "Jah - võta ühendust meie AI-assistendiga veebichatis.",
    },
    11: {
      question: "Toetamata formaat?",
      answer: "Kasuta konverterit või küsi kiiret abi meie AI-assistendilt veebichatis.",
    },
    13: {
      question: "Ma ei saanud oma e-kirja kätte. Mida teha?",
      answer:
        "Töötlemisaeg võib sõltuvalt järjekorra koormusest erineda. Ära muretse - su fail on turvaliselt alles. Niipea kui kõik on valmis, saad teavituse või otsese allalaadimislingi.",
    },
  },
  fi: {
    6: {
      question: "Voinko valita toisen äänen?",
      answer: "Ei - kloonaamme vain omaa ääntäsi.",
    },
    8: {
      question: "Miten saan videoni?",
      answer: "Välitön lataus selaimessa + kopio sähköpostiisi maksun jälkeen.",
    },
    9: {
      question: "Alennuksia suurille tilauksille?",
      answer: "Kyllä - ota yhteyttä AI-avustajaamme verkkokeskustelussa.",
    },
    11: {
      question: "Formaattia ei tueta?",
      answer: "Käytä muunninta tai pyydä nopeaa apua AI-avustajaltamme verkkokeskustelussa.",
    },
    13: {
      question: "En saanut sähköpostia. Mitä teen?",
      answer:
        "Käsittelyaika voi vaihdella jonon ruuhkan mukaan. Ei hätää - tiedostosi on turvassa. Saat ilmoituksen tai suoran latauslinkin heti, kun se on valmis.",
    },
  },
  fr: {
    6: {
      question: "Puis-je choisir une autre voix ?",
      answer: "Non - nous clonons uniquement votre propre voix.",
    },
    8: {
      question: "Comment est-ce que je reçois ma vidéo ?",
      answer: "Téléchargement instantané dans votre navigateur + copie envoyée par e-mail après le paiement.",
    },
    9: {
      question: "Des remises pour les grosses commandes ?",
      answer: "Oui - contactez notre assistant IA dans le chat en ligne.",
    },
    11: {
      question: "Format non pris en charge ?",
      answer: "Utilisez un convertisseur ou demandez une aide rapide à notre assistant IA dans le chat en ligne.",
    },
    13: {
      question: "Je n'ai pas reçu mon e-mail. Que dois-je faire ?",
      answer:
        "Le délai de traitement peut varier selon le volume de la file d'attente. Pas d'inquiétude - votre fichier est en sécurité. Vous recevrez soit une notification, soit un lien de téléchargement direct dès qu'il sera prêt.",
    },
  },
  ga: {
    6: {
      question: "An féidir liom guth eile a roghnú?",
      answer: "Ní féidir - ní chlónaímid ach do ghuth féin.",
    },
    8: {
      question: "Conas a gheobhaidh mé mo fhíseán?",
      answer: "Íoslódáil láithreach i do bhrabhsálaí + cóip chuig do ríomhphost tar éis íocaíochta.",
    },
    9: {
      question: "Lascainí ar orduithe móra?",
      answer: "Sea - déan teagmháil lenár gcúntóir AI sa chomhrá ar líne.",
    },
    11: {
      question: "Formáid nach dtacaítear léi?",
      answer: "Úsáid tiontaire nó faigh cabhair thapa ónár gcúntóir AI sa chomhrá ar líne.",
    },
    13: {
      question: "Ní bhfuair mé mo ríomhphost. Cad ba cheart dom a dhéanamh?",
      answer:
        "Féadfaidh an t-am próiseála athrú de réir líon na n-iarratas sa scuaine. Ná bí buartha - tá do chomhad sábháilte. Gheobhaidh tú fógra nó nasc íoslódála díreach nuair a bheidh sé réidh.",
    },
  },
  hr: {
    6: {
      question: "Mogu li odabrati drugi glas?",
      answer: "Ne - kloniramo samo vaš vlastiti glas.",
    },
    8: {
      question: "Kako ću dobiti svoj video?",
      answer: "Trenutačno preuzimanje u pregledniku + kopija na vaš e-mail nakon plaćanja.",
    },
    9: {
      question: "Popusti za veće narudžbe?",
      answer: "Da - javite se našem AI asistentu u online chatu.",
    },
    11: {
      question: "Nepodržan format?",
      answer: "Upotrijebite konverter ili potražite brzu pomoć od našeg AI asistenta u online chatu.",
    },
    13: {
      question: "Nisam dobio e-mail. Što da radim?",
      answer:
        "Vrijeme obrade može varirati ovisno o gužvi u redu. Ne brinite - vaša je datoteka sigurna. Dobit ćete obavijest ili izravni link za preuzimanje čim bude spreman.",
    },
  },
  hu: {
    6: {
      question: "Választhatok másik hangot?",
      answer: "Nem - csak a saját hangodat klónozzuk.",
    },
    8: {
      question: "Hogyan kapom meg a videómat?",
      answer: "Azonnali letöltés a böngészőben + másolat e-mailben a fizetés után.",
    },
    9: {
      question: "Vannak kedvezmények nagyobb rendelésekre?",
      answer: "Igen - írj AI-asszisztensünknek az online chatben.",
    },
    11: {
      question: "Nem támogatott formátum?",
      answer: "Használj konvertálót, vagy kérj gyors segítséget AI-asszisztensünktől az online chatben.",
    },
    13: {
      question: "Nem kaptam meg az e-mailt. Mit tegyek?",
      answer:
        "A feldolgozási idő a várólista terhelésétől függően változhat. Ne aggódj - a fájlod biztonságban van. Amint elkészül, értesítést vagy közvetlen letöltési linket kapsz.",
    },
  },
  it: {
    6: {
      question: "Posso scegliere un'altra voce?",
      answer: "No - cloniamo solo la tua voce.",
    },
    8: {
      question: "Come ricevo il mio video?",
      answer: "Download immediato nel browser + copia via email dopo il pagamento.",
    },
    9: {
      question: "Sconti per ordini grandi?",
      answer: "Sì - contatta il nostro assistente AI nella chat online.",
    },
    11: {
      question: "Formato non supportato?",
      answer: "Usa un convertitore oppure chiedi aiuto rapido al nostro assistente AI nella chat online.",
    },
    13: {
      question: "Non ho ricevuto la mia email. Cosa devo fare?",
      answer:
        "I tempi di elaborazione possono variare in base al volume della coda. Non preoccuparti - il tuo file è al sicuro. Riceverai una notifica oppure un link diretto per il download non appena sarà pronto.",
    },
  },
  ja: {
    6: {
      question: "別の声を選べますか?",
      answer: "いいえ。クローンできるのはあなた自身の声だけです。",
    },
    8: {
      question: "動画はどう受け取れますか?",
      answer: "ブラウザですぐにダウンロードでき、支払い後にメールにもコピーが届きます。",
    },
    9: {
      question: "大量注文の割引はありますか?",
      answer: "はい。オンラインチャットでAIアシスタントにご連絡ください。",
    },
    11: {
      question: "未対応の形式ですか?",
      answer: "コンバーターを使うか、オンラインチャットでAIアシスタントにすぐ相談してください。",
    },
    13: {
      question: "メールが届きません。どうすればいいですか?",
      answer:
        "処理時間はキューの混雑状況によって変わることがあります。ご安心ください。ファイルは安全に保管されています。準備ができ次第、通知または直接ダウンロードリンクをお送りします。",
    },
  },
  ko: {
    6: {
      question: "다른 목소리를 고를 수 있나요?",
      answer: "아니요. 우리는 오직 여러분 자신의 목소리만 클론해요.",
    },
    8: {
      question: "내 영상은 어떻게 받나요?",
      answer: "브라우저에서 바로 다운로드할 수 있고, 결제 후 이메일로도 사본이 전송돼요.",
    },
    9: {
      question: "대량 주문 할인도 있나요?",
      answer: "네. 온라인 채팅에서 AI 어시스턴트에게 문의해 주세요.",
    },
    11: {
      question: "지원되지 않는 형식인가요?",
      answer: "변환기를 사용하거나 온라인 채팅에서 AI 어시스턴트에게 빠르게 도움을 요청해 주세요.",
    },
    13: {
      question: "이메일을 받지 못했어요. 어떻게 해야 하나요?",
      answer:
        "처리 시간은 대기열 상황에 따라 달라질 수 있어요. 걱정하지 마세요. 파일은 안전하게 보관되어 있어요. 준비되는 즉시 알림이나 직접 다운로드 링크를 받게 됩니다.",
    },
  },
  lt: {
    6: {
      question: "Ar galiu pasirinkti kitą balsą?",
      answer: "Ne - mes klonuojame tik jūsų pačių balsą.",
    },
    8: {
      question: "Kaip gausiu savo vaizdo įrašą?",
      answer: "Akimirksniu atsisiųsite naršyklėje, o po apmokėjimo kopija bus atsiųsta ir į jūsų el. paštą.",
    },
    9: {
      question: "Ar taikote nuolaidas didesniems užsakymams?",
      answer: "Taip - susisiekite su mūsų AI asistentu internetiniame pokalbyje.",
    },
    11: {
      question: "Nepalaikomas formatas?",
      answer: "Naudokite konverterį arba gaukite greitą pagalbą iš mūsų AI asistento internetiniame pokalbyje.",
    },
    13: {
      question: "Negavau el. laiško. Ką daryti?",
      answer:
        "Apdorojimo laikas gali skirtis priklausomai nuo eilės apkrovos. Nesijaudinkite - jūsų failas saugus. Kai tik viskas bus paruošta, gausite pranešimą arba tiesioginę atsisiuntimo nuorodą.",
    },
  },
  lv: {
    6: {
      question: "Vai es varu izvēlēties citu balsi?",
      answer: "Nē - mēs klonējam tikai jūsu pašu balsi.",
    },
    8: {
      question: "Kā es saņemšu savu video?",
      answer: "Tūlītēja lejupielāde pārlūkā + kopija uz jūsu e-pastu pēc apmaksas.",
    },
    9: {
      question: "Vai ir atlaides lieliem pasūtījumiem?",
      answer: "Jā - sazinieties ar mūsu AI asistentu tiešsaistes čatā.",
    },
    11: {
      question: "Neatbalstīts formāts?",
      answer: "Izmantojiet konvertētāju vai saņemiet ātru palīdzību no mūsu AI asistenta tiešsaistes čatā.",
    },
    13: {
      question: "Es nesaņēmu e-pastu. Ko man darīt?",
      answer:
        "Apstrādes laiks var atšķirties atkarībā no rindas noslodzes. Neuztraucieties - jūsu fails ir drošībā. Tiklīdz viss būs gatavs, saņemsiet paziņojumu vai tiešo lejupielādes saiti.",
    },
  },
  mt: {
    6: {
      question: "Nista' nagħżel vuċi oħra?",
      answer: "Le - aħna nikklonaw biss il-vuċi tiegħek stess.",
    },
    8: {
      question: "Kif nirċievi l-vidjow tiegħi?",
      answer: "Tniżżil immedjat fil-browser + kopja fuq l-email tiegħek wara l-ħlas.",
    },
    9: {
      question: "Skonti għal ordnijiet kbar?",
      answer: "Iva - ikkuntattja lill-assistent AI tagħna fiċ-chat online.",
    },
    11: {
      question: "Format mhux appoġġjat?",
      answer: "Uża converter jew ikseb għajnuna malajr mingħand l-assistent AI tagħna fiċ-chat online.",
    },
    13: {
      question: "Ma rċevejtx l-email tiegħi. X'għandi nagħmel?",
      answer:
        "Il-ħin tal-ipproċessar jista' jvarja skont kemm hemm xogħol fil-kju. Tinkwetax - il-fajl tiegħek hu sigur. Tirċievi notifika jew link dirett għat-tniżżil hekk kif ikun lest.",
    },
  },
  nl: {
    6: {
      question: "Kan ik een andere stem kiezen?",
      answer: "Nee - we klonen alleen je eigen stem.",
    },
    8: {
      question: "Hoe krijg ik mijn video?",
      answer: "Direct downloaden in je browser + een kopie per e-mail na betaling.",
    },
    9: {
      question: "Kortingen voor grote bestellingen?",
      answer: "Ja - neem contact op met onze AI-assistent in de online chat.",
    },
    11: {
      question: "Niet-ondersteund formaat?",
      answer: "Gebruik een converter of vraag snel hulp aan onze AI-assistent in de online chat.",
    },
    13: {
      question: "Ik heb mijn e-mail niet ontvangen. Wat moet ik doen?",
      answer:
        "De verwerkingstijd kan variëren door drukte in de wachtrij. Geen zorgen - je bestand is veilig. Je ontvangt een melding of een directe downloadlink zodra het klaar is.",
    },
  },
  pl: {
    6: {
      question: "Czy mogę wybrać inny głos?",
      answer: "Nie - klonujemy tylko Twój własny głos.",
    },
    8: {
      question: "Jak dostanę swoje wideo?",
      answer: "Natychmiastowe pobranie w przeglądarce + kopia wysłana na Twój e-mail po płatności.",
    },
    9: {
      question: "Zniżki przy większych zamówieniach?",
      answer: "Tak - skontaktuj się z naszym asystentem AI na czacie online.",
    },
    11: {
      question: "Nieobsługiwany format?",
      answer: "Użyj konwertera albo poproś o szybką pomoc naszego asystenta AI na czacie online.",
    },
    13: {
      question: "Nie dostałem e-maila. Co mam zrobić?",
      answer:
        "Czas przetwarzania może się różnić w zależności od obciążenia kolejki. Spokojnie - Twój plik jest bezpieczny. Gdy wszystko będzie gotowe, dostaniesz powiadomienie albo bezpośredni link do pobrania.",
    },
  },
  pt: {
    6: {
      question: "Posso escolher outra voz?",
      answer: "Não - clonamos apenas a sua própria voz.",
    },
    8: {
      question: "Como recebo meu vídeo?",
      answer: "Download instantâneo no navegador + cópia no seu e-mail após o pagamento.",
    },
    9: {
      question: "Descontos para pedidos grandes?",
      answer: "Sim - fale com nosso assistente de IA no chat online.",
    },
    11: {
      question: "Formato não compatível?",
      answer: "Use um conversor ou peça ajuda rápida ao nosso assistente de IA no chat online.",
    },
    13: {
      question: "Não recebi meu e-mail. O que devo fazer?",
      answer:
        "O tempo de processamento pode variar conforme o volume da fila. Não se preocupe - seu arquivo está seguro. Você receberá uma notificação ou um link direto de download assim que estiver pronto.",
    },
  },
  ro: {
    6: {
      question: "Pot alege o altă voce?",
      answer: "Nu - clonăm doar propria ta voce.",
    },
    8: {
      question: "Cum primesc videoclipul meu?",
      answer: "Descărcare instant în browser + copie pe e-mail după plată.",
    },
    9: {
      question: "Reduceri pentru comenzi mari?",
      answer: "Da - contactează asistentul nostru AI în chatul online.",
    },
    11: {
      question: "Format nesuportat?",
      answer: "Folosește un convertor sau cere ajutor rapid de la asistentul nostru AI în chatul online.",
    },
    13: {
      question: "Nu am primit e-mailul. Ce ar trebui să fac?",
      answer:
        "Timpul de procesare poate varia în funcție de volumul din coadă. Nu-ți face griji - fișierul tău este în siguranță. Vei primi fie o notificare, fie un link direct de descărcare imediat ce este gata.",
    },
  },
  ru: {
    6: {
      question: "Могу ли я выбрать другой голос?",
      answer: "Нет - мы клонируем только ваш собственный голос.",
    },
    8: {
      question: "Как я получу свое видео?",
      answer: "Сразу скачаете его в браузере, а после оплаты копия придет на ваш email.",
    },
    9: {
      question: "Есть ли скидки для крупных заказов?",
      answer: "Да - напишите нашему AI-ассистенту в онлайн-чате.",
    },
    11: {
      question: "Неподдерживаемый формат?",
      answer: "Используйте конвертер или получите быструю помощь у нашего AI-ассистента в онлайн-чате.",
    },
    13: {
      question: "Я не получил письмо. Что делать?",
      answer:
        "Время обработки может отличаться в зависимости от загрузки очереди. Не волнуйтесь - ваш файл в безопасности. Как только все будет готово, вы получите уведомление или прямую ссылку на скачивание.",
    },
  },
  sk: {
    6: {
      question: "Môžem si vybrať iný hlas?",
      answer: "Nie - klonujeme iba váš vlastný hlas.",
    },
    8: {
      question: "Ako dostanem svoje video?",
      answer: "Okamžité stiahnutie v prehliadači + kópia do e-mailu po zaplatení.",
    },
    9: {
      question: "Zľavy pri väčších objednávkach?",
      answer: "Áno - kontaktujte nášho AI asistenta v online chate.",
    },
    11: {
      question: "Nepodporovaný formát?",
      answer: "Použite konvertor alebo si vyžiadajte rýchlu pomoc od nášho AI asistenta v online chate.",
    },
    13: {
      question: "Nedostal som e-mail. Čo mám robiť?",
      answer:
        "Čas spracovania sa môže líšiť podľa vyťaženia frontu. Nebojte sa - váš súbor je v bezpečí. Hneď ako bude pripravený, dostanete notifikáciu alebo priamy odkaz na stiahnutie.",
    },
  },
  sl: {
    6: {
      question: "Ali lahko izberem drug glas?",
      answer: "Ne - kloniramo samo vaš lasten glas.",
    },
    8: {
      question: "Kako dobim svoj video?",
      answer: "Takojšen prenos v brskalniku + kopija na vaš e-mail po plačilu.",
    },
    9: {
      question: "Popusti za večja naročila?",
      answer: "Da - pišite našemu AI-asistentu v spletnem chatu.",
    },
    11: {
      question: "Nepodprt format?",
      answer: "Uporabite pretvornik ali pa hitro pomoč poiščite pri našem AI-asistentu v spletnem chatu.",
    },
    13: {
      question: "Nisem prejel e-pošte. Kaj naj storim?",
      answer:
        "Čas obdelave se lahko razlikuje glede na obremenitev vrste. Brez skrbi - vaša datoteka je varna. Takoj ko bo pripravljena, boste prejeli obvestilo ali neposredno povezavo za prenos.",
    },
  },
  sv: {
    6: {
      question: "Kan jag välja en annan röst?",
      answer: "Nej - vi klonar bara din egen röst.",
    },
    8: {
      question: "Hur får jag min video?",
      answer: "Direkt nedladdning i din webbläsare + en kopia till din e-post efter betalning.",
    },
    9: {
      question: "Rabatter för stora beställningar?",
      answer: "Ja - kontakta vår AI-assistent i onlinechatten.",
    },
    11: {
      question: "Formatet stöds inte?",
      answer: "Använd en konverterare eller få snabb hjälp av vår AI-assistent i onlinechatten.",
    },
    13: {
      question: "Jag fick inte mitt e-postmeddelande. Vad ska jag göra?",
      answer:
        "Bearbetningstiden kan variera beroende på hur lång kön är. Oroa dig inte - din fil är säker. Du får antingen en avisering eller en direkt nedladdningslänk så snart den är klar.",
    },
  },
  zh: {
    6: {
      question: "我可以选择其他声音吗？",
      answer: "不可以，我们只克隆你自己的声音。",
    },
    8: {
      question: "我怎么拿到我的视频？",
      answer: "可在浏览器中立即下载，付款后也会同步发送一份到你的邮箱。",
    },
    9: {
      question: "大批量订单有优惠吗？",
      answer: "有的，请在在线聊天中联系 AI 助手。",
    },
    11: {
      question: "格式不支持？",
      answer: "请先用转换工具处理，或者在在线聊天里向 AI 助手快速求助。",
    },
    13: {
      question: "我没有收到邮件，该怎么办？",
      answer:
        "处理时间可能会因为队列拥挤而有所波动。别担心，你的文件是安全的。一旦处理完成，你会收到通知或直接下载链接。",
    },
  },
};

export const homeFaqOverrides = Object.fromEntries(
  Object.entries(baseHomeFaqOverrides).map(([language, entries]) => [
    language,
    {
      ...shiftHomeFaqEntries(entries, 2),
      ...(localizedServiceFaqOverrides[language as SupportedLanguage] ?? {}),
      3: paymentBeforeFaqOverrides[language as SupportedLanguage],
      4: refundFaqOverrides[language as SupportedLanguage],
      10: supportFaqOverrides[language as SupportedLanguage],
      12: pronunciationFaqOverrides[language as SupportedLanguage],
    },
  ]),
) as Partial<Record<SupportedLanguage, Partial<Record<number, HomeFaqOverrideEntry>>>>;
